// Données des Rendez-vous pédagogiques AAC (Apprentissage Anticipé de la Conduite)
// Cahier des charges officiel : Arrêté du 20 avril 2012 modifié (REMC) — annexe 3
// Chaque RVP = un support de présentation vidéoprojecteur, parents + élève présents.

import { RDV_PREALABLE_SLIDES } from './rdvPrealableSlides';
import { GUIDE_AAC_PREALABLE_SLIDES, GUIDE_AAC_RVP1_SLIDES, GUIDE_AAC_RVP2_SLIDES } from './guideAacSlides';


/* ────────────── Types de slides ────────────── */
// { type: 'title', title, subtitle, kicker?, image? }
// { type: 'section', number, title, note? }
// { type: 'content', title, bullets[], footnote? }
// { type: 'stat', number, label, source, context? }
// { type: 'qcm', question, options: [{ text, correct? }], explanation, engagement? }
// { type: 'video', title, youtube_id, note? }
// { type: 'image', caption, image_url }
// { type: 'quote', text, author }
// { type: 'engagement', title, items: string[], signature? }
// { type: 'cta', title, description, next_action }

export const RVP_LIST = [
  {
    slug: 'aac-rdv-prealable',
    code: 'RDV PRÉALABLE',
    title: 'Rendez-vous préalable · remise AFFI',
    subtitle: 'Le point de départ obligatoire — avant le premier kilomètre en conduite accompagnée',
    duration: '2h (théorie + conduite en circulation)',
    kilometrage: 'Fin de formation initiale · 0 km en autonomie',
    color: 'emerald',
    icon: 'Flag',
    formation: 'AAC',
    description: "Se déroule EN VOITURE avec l'élève et l'accompagnateur. Travaille-le comme un bilan de fin de formation : cases à cocher (20 critères), récap automatique des éléments à corriger, et conseils à l'accompagnateur pour gérer l'élève sans pédales. La présentation ci-dessous sert de support si besoin.",
    officiel: true,
    extraLink: { to: '/enseignant/examen-blanc-grille?tab=bilan&type=rdv_prealable', label: 'Ouvrir la grille bilan · à cocher en voiture' },
  },
  {
    slug: 'aac-rvp-1',
    code: 'RVP 1',
    title: 'Rendez-vous pédagogique n°1',
    subtitle: "1er rendez-vous officiel obligatoire — après environ 1 000 km parcourus",
    duration: '3h (1h conduite + 2h théorie collective)',
    kilometrage: "Entre 4 et 6 mois après l'AFFI · ≥ 1 000 km",
    color: 'sky',
    icon: 'Rocket',
    formation: 'AAC',
    description: "1er RVP officiel AAC (Arrêté 22/03/1993). Séance collective en salle avec 1h de conduite autonome évaluée. Contenus officiels : ceinture · alcool/stupéfiants · fatigue/vigilance · vitesse · assurances. Accompagnateur obligatoirement présent.",
    officiel: true,
  },
  {
    slug: 'aac-rvp-2',
    code: 'RVP 2',
    title: 'Rendez-vous pédagogique n°2',
    subtitle: 'Rendez-vous final obligatoire — après 3 000 km et 1 an minimum',
    duration: '3h (1h conduite + 2h théorie collective)',
    kilometrage: "≥ 3 000 km parcourus · ≥ 1 an après l'AFFI",
    color: 'amber',
    icon: 'Compass',
    formation: 'AAC',
    description: "2nd RVP officiel AAC. Bilan des acquis, préparation à l'examen pratique dès 17 ans (loi 01/01/2024). Approfondissement : conduite nuit, autoroute, intempéries. Accompagnateur obligatoirement présent.",
    officiel: true,
  },
  {
    slug: 'aac-rvp-3',
    code: 'RVP 3',
    title: 'Rendez-vous pédagogique n°3 (optionnel)',
    subtitle: 'À la demande — sur conseil de l\'école, du candidat ou de l\'accompagnateur',
    duration: 'Variable (1h à 3h)',
    kilometrage: 'Aucune condition · à la demande',
    color: 'emerald',
    icon: 'Trophy',
    formation: 'AAC',
    description: "RVP optionnel proposé par l'école ou demandé par la famille en cas de difficulté, de longue interruption, ou de préparation à l'examen. Format modulable selon les besoins identifiés.",
    officiel: false,
  },
  {
    slug: 'am-synthese',
    code: 'AM · SYNTHÈSE',
    title: 'Formation AM · Séquence 7 · Synthèse avec les parents',
    subtitle: '7e et dernière séquence obligatoire — famille présente',
    duration: '1h · théorie collective avec parent(s) obligatoirement',
    kilometrage: "En fin de formation AM (8h) · après les 7h précédentes",
    color: 'red',
    icon: 'Trophy',
    formation: 'AM',
    description: "Séquence 7 obligatoire de la formation AM (BSR cyclomoteur 50cc / voiturette). Bilan formation, sensibilisation famille, remise du livret. Arrêté du 8 novembre 2012 modifié. Parent obligatoirement présent.",
    officiel: true,
  },
];

/* ══════════════════════════════════════════════════════════════════
   RVP 1 — Trame REMC 9 phases + Programme officiel AAC
   (Arrêté du 22 mars 1993 modifié · durée min 2h)
   Format : ~50 slides pour tenir 2h avec un groupe de 5-10 personnes
   ══════════════════════════════════════════════════════════════════ */

export const RVP_1_SLIDES = [
  /* ══════════ PHASE 1 · Accueil & Présentation (5 min) ══════════ */
  {
    type: 'title',
    kicker: 'Rendez-vous pédagogique n°1 · AAC · Officiel',
    title: 'Bienvenue',
    subtitle: "1er RVP officiel · après ≥ 1 000 km · 3h (1h conduite + 2h théorie collective)",
    footer: "Élève · Accompagnateur(s) obligatoires · Enseignant ECSR",
    notes: "⏱️ 2 min.\n\nAccueille chaque famille par leur prénom. Vérifie que le livret d'apprentissage est présent pour chaque élève.\n\nRappelle les règles de vie : téléphones en silencieux, questions à tout moment, pas de jugement.\n\nAnnonce le programme des 2 prochaines heures : théorie collective + engagements.",
  },
  {
    type: 'content',
    kicker: 'Phase 1 · Présentation & Déroulé',
    title: "Programme des 3 heures",
    bullets: [
      "🚗 Partie A · 1h conduite en circulation autonome — évaluation du duo (avant OU après la salle)",
      "📋 Partie B · 2h théorie collective — les 9 phases ci-dessous",
      "🎤 Phase 1 · Présentation & déroulé — 5 min",
      "🤝 Phase 2 · Découverte / tour de table — 15 min",
      "📝 Phase 3 · Évaluation générale (diagnostic) — 10 min",
      "🎯 Phase 4 · Évaluation spécifique AAC — 5 min",
      "🚦 Phase 5 · Objectif de la séance — 3 min",
      "📚 Phase 6 · Cours officiel (5 thèmes majeurs) — 60 min",
      "🔎 Phase 7 · Bilan de séance — 10 min",
      "✅ Phase 8 · Évaluation finale — 10 min",
      "🛡️ Phase 9 · Message Sécurité Routière — 5 min",
    ],
    footnote: "Règles de vie : bienveillance · questions à tout moment · téléphone en silencieux · pause de 5 min à mi-parcours. Programme conforme à l'Arrêté du 22 mars 1993 modifié.",
    notes: "⏱️ 3 min. Explique clairement le déroulé pour rassurer.\n\nAnnonce la pause de 5 min à mi-parcours (~1h) pour café/toilettes.\n\nMontre le QR code sondage : « Vous allez pouvoir voter aux QCM avec votre téléphone. C'est anonyme et collectif. »",
  },

  /* ══════════ PHASE 2 · Découverte / tour de table (15 min) ══════════ */
  {
    type: 'tour-de-table',
    title: "Faisons connaissance",
    intro: "Chacun se présente et répond à 4 questions. On écoute sans juger. L'objectif : identifier les attentes, les représentations, les craintes.",
    questions: [
      "Qui êtes-vous ? Prénom, âge, situation (élève, parent, tuteur…)",
      "Pourquoi avez-vous choisi la formule AAC ?",
      "Qu'est-ce qui vous inquiète le plus dans la conduite accompagnée ?",
      "Qu'attendez-vous concrètement de ce rendez-vous d'aujourd'hui ?",
    ],
    note: "On garde vos attentes en tête pour y revenir ensemble en fin de séance.",
    notes: "⏱️ 10 min max.\n\nCommence PAR TOI : « Je m'appelle X, moniteur ECSR depuis Y années. »\n\nRègle d'or : écouter sans juger. Reformule chaque intervention brièvement pour montrer que tu as entendu.\n\nSi un parent monopolise, redirige avec bienveillance vers l'élève : « Et toi, qu'en penses-tu ? »\n\nNote AU TABLEAU les 3-4 attentes récurrentes → on y revient en Phase 7 (Bilan) pour boucler.",
  },
  {
    type: 'tour-de-table',
    title: "Représentations · La conduite pour vous, c'est quoi ?",
    intro: "En 1 mot ou 1 phrase courte, chacun exprime ce que représente la conduite. Objectif : faire émerger les représentations collectives (liberté, danger, autonomie, fierté, angoisse…).",
    questions: [
      "Élève : « La conduite, pour moi, c'est… »",
      "Accompagnateur : « Quand je pense à mon fils/ma fille au volant, je ressens… »",
      "Duo : « Notre plus grande peur commune, c'est… »",
      "Duo : « Notre plus grande fierté à venir, c'est… »",
    ],
    note: "Chaque mot compte. Liberté ↔ responsabilité · Peur ↔ préparation. C'est le fil rouge d'aujourd'hui.",
    notes: "⏱️ 5 min.\n\nCe temps est CRUCIAL pour créer le lien émotionnel. Les parents ont souvent peur de laisser leur enfant conduire — laisse-les l'exprimer.\n\nReformule chaque émotion : « Vous ressentez de l'appréhension, c'est normal, c'est votre rôle de parent. »\n\nBoucle : « Aujourd'hui, l'objectif est de transformer ces peurs en compétences. »",
  },

  /* ══════════ PHASE 3 · Évaluation générale · Diagnostic (10 min) ══════════ */
  {
    type: 'qcm',
    question: "Diagnostic n°1 · À quel âge peut-on démarrer la formation AAC en France ?",
    options: [
      { text: '14 ans', correct: false },
      { text: '15 ans', correct: true },
      { text: '16 ans', correct: false },
      { text: '17 ans', correct: false },
    ],
    explanation: "Depuis 2014, la formation AAC peut démarrer dès 15 ans (contre 16 auparavant). C'est le principal avantage de la formule : commencer tôt pour accumuler de l'expérience avant le permis dès 17 ans (loi 01/01/2024).",
    notes: "⏱️ 2 min avec vote.\n\nLaisse 30 secondes pour voter. Puis clique Révéler.\n\nInsiste : « 15 ans, c'est très jeune mais c'est un choix pédagogique. Plus on commence tôt, plus on apprend en profondeur. »",
  },
  {
    type: 'qcm',
    question: "Diagnostic n°2 · Depuis le 01/01/2024, à partir de quel âge peut-on conduire SEUL en France ?",
    options: [
      { text: '16 ans', correct: false },
      { text: '17 ans', correct: true },
      { text: '18 ans', correct: false },
      { text: 'Uniquement à 18 ans en AAC', correct: false },
    ],
    explanation: "Depuis le 1er janvier 2024, l'âge légal de conduite SEUL est passé de 18 à 17 ans, aussi bien en filière classique qu'en AAC. Le probatoire reste ensuite de 2 ans pour AAC, 3 ans pour la classique.",
    notes: "⏱️ 2 min.\n\nLa nouveauté 2024 est importante. Beaucoup de parents pensent encore 18 ans.\n\nPrécise : « Conduire seul dès 17 ans est possible, mais l'AAC continue de recommander l'accompagnement au maximum. »",
  },
  {
    type: 'qcm',
    question: "Diagnostic n°3 · Quelle est la principale cause de mortalité chez les 18-24 ans en France ?",
    options: [
      { text: 'La drogue et la violence', correct: false },
      { text: 'Les accidents de la route', correct: true },
      { text: 'Le suicide', correct: false },
      { text: 'Les maladies', correct: false },
    ],
    explanation: "Les accidents de la route sont la 1re cause de mortalité chez les 18-24 ans en France. Cette tranche d'âge représente 8 % de la population mais 16 % des tués (source ONISR). C'est ce qui justifie l'existence même de l'AAC.",
    notes: "⏱️ 3 min.\n\nSlide CHOC. Laisse un long silence après le vote.\n\nInsiste : « Ce n'est pas pour vous faire peur, c'est un constat. Et c'est ce qui rend votre présence aujourd'hui essentielle. »\n\nSi émotion dans la salle, laisse s'exprimer. Ne coupe pas.",
  },

  /* ══════════ PHASE 4 · Évaluation spécifique AAC (5 min) ══════════ */
  {
    type: 'qcm',
    question: "Éval spécifique · Combien de kilomètres minimum doivent être parcourus en phase accompagnée AAC ?",
    options: [
      { text: '1 000 km', correct: false },
      { text: '2 000 km', correct: false },
      { text: '3 000 km', correct: true },
      { text: '5 000 km', correct: false },
    ],
    explanation: "Le kilométrage minimum de la phase accompagnée est de 3 000 km, à parcourir sur une durée minimale de 1 an. Ces 3000 km doivent être variés : agglomération, autoroute, nuit, intempéries, longs trajets.",
    engagement: "Note ces 3000 km sur ton livret d'apprentissage — pas de km validés = pas de RVP2 ni de RVP3.",
    notes: "⏱️ 2 min.\n\n3000 km ≈ un aller-retour Paris–Marseille × 2. C'est ambitieux mais faisable en 1 an à raison de 250 km/mois.",
  },
  {
    type: 'qcm',
    question: "Éval spécifique · Sur combien de temps MINIMUM doivent être parcourus ces 3000 km ?",
    options: [
      { text: '3 mois', correct: false },
      { text: '6 mois', correct: false },
      { text: '1 an', correct: true },
      { text: '2 ans', correct: false },
    ],
    explanation: "Durée minimum : 1 an entre la fin de la formation initiale et le passage de l'examen. Pourquoi ? Il faut avoir traversé les 4 saisons : neige, pluie, brouillard, soleil éblouissant. Chacune a ses propres pièges.",
    notes: "⏱️ 3 min. Précise : « On ne peut pas raccourcir cette année, même si tous les km sont faits. L'expérience saisonnière est irremplaçable. »",
  },

  /* ══════════ PHASE 5 · Objectif de la séance (3 min) ══════════ */
  {
    type: 'objectif',
    objectif: "Démarrer la phase accompagnée en toute sécurité, en connaissant le cadre légal, les responsabilités de chacun et les principaux risques de la route.",
    criteres: [
      "L'élève cite les 3 documents obligatoires à avoir dans le véhicule",
      "L'accompagnateur cite les critères pour être accompagnateur",
      "Le duo cite les 3 vitesses spéciales AAC (110/100/80)",
      "Le duo cite le taux d'alcool autorisé en probatoire (0,2 g/L)",
      "Le duo signe l'engagement mutuel de fin de séance",
    ],
    notes: "⏱️ 3 min.\n\nAnnonce l'objectif de façon SOLENNELLE : « À la fin de cette séance, vous saurez… »\n\nInsiste sur les 5 critères de réussite : ce sont les 5 questions que tu poseras en Phase 8 (Éval finale) pour valider que la séance a servi à quelque chose.\n\nSi un critère te semble déjà maîtrisé par le duo → tu peux accélérer la partie correspondante en Phase 6.",
  },

  /* ══════════ PHASE 6 · COURS OFFICIEL (60 min) ══════════ */
  /* ─── Bloc 1 · Cadre légal AAC (10 min) ─── */
  {
    type: 'section',
    number: '01',
    title: 'Cadre légal AAC',
    note: "Programme officiel — Arrêté du 22 mars 1993 modifié · 10 min",
  },
  {
    type: 'stat',
    number: '3000 km',
    label: "distance minimale à parcourir en phase accompagnée",
    source: "Arrêté du 22 juin 2016 · Art. R211-3 du Code de la route",
    context: "Sur une durée minimale de 1 an entre la fin de la formation initiale (20h en auto-école) et le passage de l'examen pratique dès 17 ans.",
  },
  {
    type: 'content',
    kicker: 'Point 1 · Cadre légal',
    title: "Les 3 documents obligatoires à bord du véhicule",
    bullets: [
      "Le livret d'apprentissage (rempli à jour, tenu par l'élève)",
      "L'attestation de fin de formation initiale (AFFI)",
      "L'attestation d'assurance du véhicule mentionnant explicitement la conduite accompagnée",
    ],
    footnote: "À ajouter à l'arrière du véhicule : deux disques « Conduite Accompagnée ». Absence = amende 11 € à 135 €.",
    notes: "⏱️ 5 min.\n\nMontre les 3 documents physiquement si tu les as sous la main. Rien de plus concret qu'un vrai document.\n\nRappelle que le livret d'apprentissage se remplit APRÈS chaque trajet, PAS à la fin du mois. Sinon on oublie tout.",
  },
  {
    type: 'qcm',
    question: "Bloc 1 · Que doit-on obligatoirement afficher à l'arrière du véhicule en AAC ?",
    options: [
      { text: 'Rien de particulier', correct: false },
      { text: 'Un autocollant « Jeune conducteur »', correct: false },
      { text: 'Deux disques « Conduite Accompagnée »', correct: true },
      { text: 'Le nom de l\'auto-école', correct: false },
    ],
    explanation: "DEUX disques « Conduite Accompagnée » à l'arrière (Article R211-3-1 du Code de la route). Absence = amende 11 € (à 135 € majorée). Ils préviennent les autres usagers pour qu'ils adaptent leur comportement.",
    notes: "⏱️ 2 min. Précise : c'est UN disque à l'arrière gauche + UN disque à l'arrière droit. Pas d'exception, même sur le véhicule familial.",
  },

  /* ─── Bloc 2 · Rôle de l'accompagnateur (10 min) ─── */
  {
    type: 'section',
    number: '02',
    title: "Rôle de l'accompagnateur",
    note: "Programme officiel · 10 min · Point crucial pour la famille",
  },
  {
    type: 'content',
    kicker: "Point 2 · Accompagnateur",
    title: "Qui peut être accompagnateur, et pour faire quoi ?",
    bullets: [
      "Titulaire du permis B depuis au moins 5 ans SANS interruption (pas de suspension, annulation, invalidation)",
      "Avoir l'accord ÉCRIT de son assureur (avenant gratuit à demander à la souscription)",
      "Être mentionné(e) sur le contrat AAC signé entre l'école et la famille",
      "Ne pas obligatoirement être un parent — un(e) proche titulaire du permis depuis 5 ans peut accompagner",
      "Rester passager attentif : capable de reprendre le volant à tout instant, jamais consommateur d'alcool avant un trajet",
    ],
    footnote: "Plusieurs accompagnateurs sont autorisés — chaque nom doit figurer au contrat.",
    notes: "⏱️ 5 min.\n\nDemande aux parents s'ils ont bien fait leur avenant assurance. Si non → RAPPEL urgent avant tout trajet.\n\nPlusieurs accompagnateurs = c'est TRÈS bien (variété de style de conduite = meilleure expérience). Mais chacun doit être déclaré.",
  },
  {
    type: 'qcm',
    question: "Bloc 2 · L'accompagnateur peut-il être une personne autre qu'un parent ?",
    options: [
      { text: 'Non, uniquement les parents biologiques', correct: false },
      { text: 'Oui, tout titulaire du permis B depuis 5 ans mentionné au contrat', correct: true },
      { text: 'Oui, mais uniquement de la famille proche', correct: false },
      { text: 'Non, un seul accompagnateur maximum', correct: false },
    ],
    explanation: "N'importe quelle personne titulaire du permis B depuis 5 ans peut être accompagnateur : oncle, grand-parent, ami de la famille, tuteur… Il faut simplement l'inscrire au contrat AAC et obtenir l'avenant assurance. Plusieurs accompagnateurs = souvent bénéfique (diversité de conduite).",
    notes: "⏱️ 2 min. Très bonne question à revisiter avec les grands-parents dans la salle. Souvent ils pensent qu'ils ne peuvent pas — SI, ils peuvent.",
  },
  /* ─── Slides officielles anti-oubli obligations ─── */
  {
    type: 'content',
    kicker: "Art. R211-5 · OFFICIEL",
    title: "Les 4 étapes obligatoires de l'AAC",
    bullets: [
      "Un RDV pédagogique PRÉALABLE avec l'accompagnateur (durée min. 2 h) — ce moment, aujourd'hui.",
      "Une phase de conduite accompagnée d'une durée MINIMUM d'1 an.",
      "Un kilométrage parcouru SUPÉRIEUR à 3 000 km sous la vigilance de l'accompagnateur.",
      "Deux RDV pédagogiques d'étape avec le formateur (~1 000 km puis 3 000 km).",
    ],
    footnote: "Article R211-5 II du Code de la route · Chaque étape figurera au devis et au livret d'apprentissage.",
    notes: "⏱️ 4 min.\n\nOfficialise la structure devant les parents. Rappelle : « À l'issue du 2e RDV d'étape, c'est MOI qui décide si l'élève est prêt pour l'examen — c'est une évaluation, pas une simple formalité. »",
  },
  {
    type: 'content',
    kicker: "OBLIGATIONS · À NE PAS OUBLIER",
    title: "5 conditions strictes pour l'accompagnateur",
    bullets: [
      "Permis B depuis MINIMUM 5 ans SANS interruption (aucune suspension, annulation ou invalidation).",
      "Accord ÉCRIT de l'assureur (avenant gratuit à demander à la souscription du contrat).",
      "Aucune condamnation pour délits routiers (homicide/blessures involontaires, alcool, délit de fuite…).",
      "Nom mentionné sur le contrat signé avec l'école de conduite.",
      "A participé à l'évaluation de la dernière étape de la formation initiale de l'élève.",
    ],
    footnote: "Plusieurs accompagnateurs autorisés — chaque nom doit figurer au contrat & sur l'avenant assurance.",
    notes: "⏱️ 4 min.\n\nInsiste sur le point 3 : si l'un des accompagnateurs a un délit routier au casier, il ne PEUT PAS accompagner. À vérifier avant le 1er trajet.\n\nInsiste sur le point 5 : l'accompagnateur devait AVOIR ÉTÉ PRÉSENT lors de la fin de formation initiale (article R211-5). Sinon, la conduite accompagnée n'est pas légale.",
  },
  {
    type: 'content',
    kicker: "Arrêté 29/07/2013 · LIVRET NUMÉRIQUE (2024)",
    title: "Livret d'apprentissage — désormais numérique",
    bullets: [
      "Depuis le 1er janvier 2024, le livret d'apprentissage est OBLIGATOIREMENT numérique (Arrêté du 19 déc. 2023).",
      "Il détaille les 4 compétences REMC (C1 Maîtriser · C2 Appréhender · C3 Circuler · C4 Pratiquer) et les 30+ objectifs à valider.",
      "Chaque trajet AAC doit y être consigné : date, durée, kilométrage, conditions, ressenti — À FAIRE APRÈS chaque trajet, PAS en bloc.",
      "En cas de contrôle : seul le FORMULAIRE de demande de permis (CERFA 02) justifie l'état d'élève. Le livret seul ne suffit pas.",
      "En cas de perte d'accès : l'enseignant OU l'accompagnateur peut ouvrir un nouvel accès.",
    ],
    footnote: "Élèves inscrits AVANT le 01/01/2024 : livret papier toléré.",
    notes: "⏱️ 3 min.\n\nMontre concrètement le livret numérique si possible (screenshot). Explique aux parents qu'ils peuvent (et doivent) le remplir avec leur enfant après chaque trajet — c'est un support de dialogue, pas une contrainte administrative.",
  },
  {
    type: 'qcm',
    question: "Bloc 2bis · Où figurent OBLIGATOIREMENT les 3 étapes contractuelles de l'AAC ?",
    options: [
      { text: 'Uniquement sur le livret d\'apprentissage', correct: false },
      { text: 'Sur le devis proposé par l\'école de conduite', correct: true },
      { text: 'Sur la carte grise du véhicule', correct: false },
      { text: 'Nulle part, elles sont juste dites oralement', correct: false },
    ],
    explanation: "Article R211-5 : le devis DOIT préciser (1) le RDV pédagogique préalable de 2h, (2) les 2 RDV pédagogiques obligatoires ultérieurs, (3) la formation théorique + présentation à l'examen du code. Si ce n'est pas écrit sur le devis, ce n'est pas un devis AAC légal.",
    notes: "⏱️ 2 min. Certains parents découvrent qu'ils peuvent (et doivent) exiger ces 3 lignes précises sur le devis. C'est un contrôle qualité gratuit.",
  },
  {
    type: 'tour-de-table',
    title: "Mise en situation · La communication en voiture",
    intro: "En famille, on parle rarement de comment on veut communiquer PENDANT le trajet. C'est pourtant essentiel pour que l'AAC se passe bien.",
    questions: [
      "Élève : « Comment aimerais-tu que ton accompagnateur t'aide pendant les trajets ? »",
      "Accompagnateur : « Comment vas-tu réagir si l'élève fait une erreur dangereuse ? »",
      "Duo : « Quel signal convenir en cas de désaccord au volant ? »",
      "Duo : « Quel moment convenir pour faire le point (fin de trajet, dîner, dimanche…) ? »",
    ],
    note: "Objectif : chaque duo repart avec 1 règle de communication clairement énoncée.",
    notes: "⏱️ 5 min.\n\nExercice CRUCIAL. La conduite AAC est autant une aventure familiale qu'un apprentissage technique.\n\nEncourage les duos à convenir d'un signal simple : « Je te propose qu'on convienne d'une main levée si je dois te faire une remarque immédiate — sans hausser le ton. »",
  },

  /* ─── Bloc 3 · ALCOOL (12 min) ─── */
  {
    type: 'section',
    number: '03',
    title: 'Alcool & stupéfiants',
    note: "Programme officiel · Facteur n°1 de mortalité chez les jeunes · 12 min",
  },
  {
    type: 'stat',
    number: '30 %',
    label: "des accidents mortels sur route impliquent l'alcool",
    source: "ONISR · Bilan Sécurité Routière 2022",
    context: "Sur les 3267 personnes tuées en 2022, environ 1000 le sont dans un accident où au moins un conducteur avait un taux positif. Chez les 18-24 ans, l'alcool est présent dans 40 % des accidents mortels.",
  },
  {
    type: 'content',
    kicker: 'Point 3 · Alcool',
    title: "Ce qu'il faut savoir sur l'alcool au volant",
    bullets: [
      "0,2 g/L MAX pendant TOUTE la période probatoire (AAC dès le début, permis obtenu ensuite pour 2 ans). Recommandation forte : 0 verre.",
      "Un verre standard = 10 g d'alcool ≈ 0,20 g/L (dose bar) — donc UN SEUL verre suffit à atteindre la limite",
      "L'alcool met 30 min à 1h à agir. Le dépistage se fait avant que la personne ne se sente « soûle »",
      "Café, douche froide, sport ne font PAS baisser le taux. Seul le temps le fait (environ 0,10 g/L/h)",
      "Sanction : 6 points sur 6 (probatoire) = permis invalidé + 135 € à 4500 € + confiscation véhicule possible",
    ],
    footnote: "Taux légal probatoire = 0,2 g/L (loi du 1er juillet 2015). Recommandation morale et sécuritaire : 0 verre.",
    notes: "⏱️ 5 min.\n\nInsiste sur la subtilité : 1 verre = ~0,20 g/L = déjà à la limite (0,2 g/L). Pas de « juste 1 petit verre ».\n\nDémonte les mythes : café, douche, courir — ça ne fait RIEN.\n\nParle du plan de sortie : si l'élève va faire la fête, il faut prévoir Uber/taxi/proche non buveur DÈS le départ, pas au dernier moment.",
  },
  {
    type: 'qcm',
    question: "Bloc 3 · Un conducteur en période probatoire (AAC ou classique) a droit à :",
    options: [
      { text: "0,5 g/L d'alcool dans le sang", correct: false },
      { text: '0,2 g/L maximum (loi 2015)', correct: true },
      { text: '0 g/L — aucune tolérance', correct: false },
      { text: '0,8 g/L comme les autres', correct: false },
    ],
    explanation: "Depuis le 1er juillet 2015 : 0,2 g/L pour tout permis probatoire (AAC dès le début + 2 ans après le permis, 3 ans en filière classique). Sanction : 6 points sur 6 = permis invalidé + amende 135 € à 4500 € + immobilisation véhicule possible. Recommandation forte : 0 verre — 1 verre standard suffit déjà à atteindre 0,20 g/L.",
    engagement: "En cas de doute → l'élève ET l'accompagnateur ne conduisent pas. Uber, taxi, un proche : toujours mieux que le drame.",
    notes: "⏱️ 2 min. Slide majeure. Rends l'engagement famille très concret : « Ce soir, sur le chemin du retour, vous en parlez ensemble. »",
  },
  {
    type: 'stat',
    number: 'x14',
    label: "risque d'accident mortel multiplié par 14 avec 0,8 g/L",
    source: "Sécurité Routière · 2023",
    context: "Le risque d'accident double dès 0,5 g/L, il est × 10 à 0,8 g/L et × 14 à 1,2 g/L. Ces chiffres ne prennent pas en compte les stupéfiants qui aggravent encore le risque.",
    notes: "⏱️ 2 min. Utilise ce chiffre pour reformuler : « 14 fois plus de chances de mourir. Ce n'est pas un peu, c'est monstrueusement. »",
  },
  {
    type: 'qcm',
    question: "Bloc 3 · Combien de temps faut-il pour éliminer 1 verre d'alcool standard (10 g d'alcool pur) en moyenne ?",
    options: [
      { text: '30 minutes', correct: false },
      { text: '1h30 à 2h selon la corpulence, le sexe et le repas', correct: true },
      { text: '1 heure exactement pour tout le monde', correct: false },
      { text: "15 minutes avec un café ou une douche froide", correct: false },
    ],
    explanation: "Le foie élimine l'alcool à un rythme incompressible de 0,10 à 0,15 g/L d'alcoolémie par heure. Pour un verre standard (10 g d'alcool pur = 1 dose bar), compter en moyenne 1h30 à 2h chez un adulte moyen. Femmes, personnes minces ou à jeun : élimination plus lente. Café, douche, sport, eau : INEFFICACES — seul le temps compte. Formule de Widmark : Alcoolémie = alcool (g) ÷ (poids × 0,68 pour ♂ / 0,55 pour ♀).",
    notes: "⏱️ 3 min. Point CRUCIAL — beaucoup pensent encore « 1 h par verre ». Faux et dangereux. Renvoie vers le simulateur d'alcoolémie sur codevoiture.fr/simulateur-alcool pour un calcul personnalisé. Rappel : pour permis probatoire, seuil = 0,2 g/L, PAS 0,5 g/L.",
  },
  {
    type: 'qr-quiz',
    url: (typeof window !== 'undefined' ? window.location.origin : 'https://codevoiture.fr') + '/simulateur-alcool',
    kicker: '🍷 Interactif · Formule de Widmark',
    title: 'Testez votre alcoolémie',
    subtitle: "Scannez avec votre téléphone : indiquez votre sexe, poids, boissons consommées + horaires. Le simulateur calcule votre taux et le temps avant de pouvoir reconduire.",
    footnote: "1 apéro = pas de volant. Le lendemain matin aussi vous pouvez encore être au-dessus du seuil.",
    notes: "⏱️ 5-8 min · MOMENT FORT.\n\nDemande à chaque famille de sortir son téléphone et scanner le QR. Fais-leur simuler « 2 bières + 1 verre de vin à 20h, sortie de soirée à 1h du matin ». La plupart découvrent qu'ils sont encore à 0,4-0,6 g/L → INFRACTION.\n\nPour le permis probatoire (élève) : rappelle que le seuil est 0,2 g/L (pas 0,5) → un demi-verre suffit.\n\nAncrage : la culture des « quelques bières entre potes » est encore banale — le simulateur casse cette illusion.",
  },

  /* ─── Bloc 4 · TÉLÉPHONE & FATIGUE (10 min) ─── */
  {
    type: 'section',
    number: '04',
    title: 'Téléphone & Fatigue',
    note: "Deux tueurs silencieux · Programme officiel · 10 min",
  },
  {
    type: 'stat',
    number: '5 secondes',
    label: "les yeux fermés en lisant un SMS à 90 km/h",
    source: "Sécurité Routière · Étude 2019",
    context: "Lire un SMS prend en moyenne 5 secondes. À 90 km/h, cela représente 125 mètres parcourus À L'AVEUGLE. Soit la longueur d'un terrain de football entier.",
  },
  {
    type: 'content',
    kicker: 'Point 4a · Téléphone',
    title: "Ce que dit la loi + les vraies pratiques",
    bullets: [
      "Téléphone en main au volant : interdit → 135 € + 3 points",
      "Kit mains libres (oreillette) : interdit → 135 € + 3 points depuis 2015",
      "Bluetooth intégré véhicule : autorisé mais fortement déconseillé (attention détournée)",
      "Regarder son téléphone même à l'arrêt à un feu rouge : interdit → 135 € + 3 points",
      "Le seul usage sûr : arrêté sur le côté, moteur coupé, dans un endroit sécurisé",
    ],
    footnote: "Solution : mode « Ne pas déranger au volant » sur iPhone/Android, à activer automatiquement.",
    notes: "⏱️ 4 min. Interpelle les parents : « Vous-mêmes, êtes-vous exemplaires ? Votre enfant vous voit conduire depuis 15 ans. »\n\nMontre comment activer le mode « Ne pas déranger au volant » sur ton propre téléphone si tu peux le projeter.",
  },
  {
    type: 'qcm',
    question: "Bloc 4 · Utiliser une oreillette Bluetooth pour téléphoner au volant est :",
    options: [
      { text: 'Autorisé', correct: false },
      { text: 'Interdit et sanctionné (135 € + 3 points)', correct: true },
      { text: 'Autorisé seulement en agglomération', correct: false },
      { text: 'Autorisé si l\'appel dure moins de 30 secondes', correct: false },
    ],
    explanation: "Depuis 2015, TOUT dispositif porté à l'oreille (oreillette filaire ou Bluetooth) est interdit au volant. Seul le kit intégré au véhicule via haut-parleur est toléré, mais l'usage reste fortement déconseillé (concentration détournée). En cas d'accident : responsabilité aggravée.",
    notes: "⏱️ 2 min. Beaucoup pensent encore que l'oreillette est autorisée. C'est FAUX depuis 2015.",
  },
  {
    type: 'stat',
    number: '1 sur 3',
    label: "accidents mortels sur autoroute liés à la somnolence",
    source: "ASFA · Association Autoroutes de France · 2022",
    context: "La fatigue reste sous-évaluée. Un micro-sommeil de 3 secondes à 130 km/h = 108 m parcourus totalement à l'aveugle. Pause obligatoire toutes les 2h ou dès signe de fatigue : bâillement, yeux qui piquent, difficulté à se concentrer.",
  },
  {
    type: 'content',
    kicker: 'Point 4b · Fatigue',
    title: "Les 5 signaux qui doivent te faire t'arrêter",
    bullets: [
      "Bâillement répété (>2 fois en 5 minutes)",
      "Yeux qui piquent, envie de les frotter",
      "Difficulté à se concentrer, pensées qui divaguent",
      "Difficulté à maintenir sa vitesse constante",
      "Sensation d'avoir la tête lourde ou l'esprit ailleurs",
    ],
    footnote: "Solution : PAUSE de 15-20 min avec MICRO-SIESTE. Ni café, ni musique forte : DORMIR est le seul vrai remède.",
    notes: "⏱️ 4 min. Explique la micro-sieste : 15-20 min max, siège incliné, réveil au chrono. Efficace pour 2h supplémentaires.",
  },

  /* ─── Bloc 5 · VITESSE (10 min) ─── */
  {
    type: 'section',
    number: '05',
    title: 'Vitesse',
    note: "1er facteur d'accident mortel · Programme officiel · 10 min",
  },
  {
    type: 'stat',
    number: '1 tué / 3',
    label: "sur la route lié à une vitesse excessive ou inadaptée",
    source: "ONISR · Bilan 2022",
    context: "La vitesse est présente dans 1 accident mortel sur 3. La distance de freinage double à chaque tranche de +20 km/h. À 90 km/h, il faut ~70 m pour s'arrêter sur sec, contre 130 m à 130 km/h.",
  },
  {
    type: 'content',
    kicker: 'Point 5a · Vitesses AAC',
    title: "Les vitesses spéciales à respecter",
    bullets: [
      "Autoroute : 110 km/h (au lieu de 130) — même hors AAC pendant tout le probatoire",
      "Route à double sens séparée par terre-plein central : 100 km/h (au lieu de 110)",
      "Hors agglomération : 80 km/h (au lieu de 80 depuis 2018, mais reste 80)",
      "Agglomération : 50 km/h (comme tous) — Zone 30 : 30 km/h",
      "En temps de pluie : chaque limite baisse de 10 à 20 km/h",
    ],
    footnote: "Excès > 50 km/h → 6 points + 1500 € + confiscation immédiate du véhicule. En probatoire = permis invalidé sur-le-champ.",
    notes: "⏱️ 4 min.\n\nInsiste sur les vitesses spéciales AAC : elles restent applicables pendant 2 ans APRÈS l'obtention du permis (soit 3 à 4 ans au total pour un élève AAC).",
  },
  {
    type: 'qcm',
    question: "Bloc 5 · En AAC, sur autoroute, la vitesse maximale autorisée est :",
    options: [
      { text: '90 km/h', correct: false },
      { text: '110 km/h', correct: true },
      { text: '130 km/h', correct: false },
      { text: 'La même que les autres conducteurs', correct: false },
    ],
    explanation: "En AAC (et pour tout conducteur en période probatoire) : autoroute 110 · voie rapide 2×2 = 100 · hors agglo 80 · agglo 50. Les disques « conduite accompagnée » à l'arrière signalent aux autres usagers.",
    engagement: "L'élève ET l'accompagnateur s'engagent à respecter les vitesses réduites — même quand l'accompagnateur conduit un autre véhicule (habitudes de famille).",
    notes: "⏱️ 2 min. Insiste : 110 km/h même si tous les autres roulent à 130. Le disque à l'arrière prévient les autres, on ne se fait pas klaxonner injustement.",
  },
  {
    type: 'content',
    kicker: 'Point 5b · Distance de freinage',
    title: "La règle des 6 (calcul rapide)",
    bullets: [
      "Distance de freinage = vitesse × 6 (approximation route sèche)",
      "50 km/h : 5 × 6 = 30 m (~ 6 voitures)",
      "90 km/h : 9 × 6 = 54 m (~ 10 voitures)",
      "110 km/h : 11 × 6 = 66 m (~ 12 voitures)",
      "130 km/h : 13 × 6 = 78 m (~ 14 voitures)",
    ],
    footnote: "Sur mouillé : × 1,5. Sur verglas : × 5. Un ABS ne raccourcit pas la distance, il permet juste de garder la trajectoire.",
    notes: "⏱️ 4 min.\n\nMontre au tableau ou sur un dessin la comparaison 50 vs 130. Choc visuel garanti.",
  },

  /* ─── Bloc 6 · ASSURANCE (8 min) ─── */
  {
    type: 'section',
    number: '06',
    title: 'Assurance & Responsabilités',
    note: "Programme officiel · 8 min",
  },
  {
    type: 'content',
    kicker: 'Point 6 · Assurance',
    title: "Ce qu'il faut vérifier auprès de votre assureur",
    bullets: [
      "L'AAC doit être explicitement mentionnée dans le contrat (avenant gratuit obligatoire)",
      "Vérifier la franchise appliquée en cas d'accident responsable de l'élève",
      "Certains assureurs offrent un bonus fidélité si l'élève souscrit chez eux à la fin de l'AAC",
      "En cas d'accident : l'accompagnateur est CIVILEMENT considéré comme le conducteur (il doit être en état de reprendre les commandes à tout instant)",
      "Conserver précieusement le livret d'apprentissage — il prouve la formation reçue",
    ],
    notes: "⏱️ 4 min.\n\nRappelle : l'avenant est GRATUIT. Aucun assureur ne peut le facturer. S'il vous demande de l'argent = changez d'assureur.",
  },
  {
    type: 'qcm',
    question: "Bloc 6 · En cas d'accident responsable pendant un trajet AAC, qui est civilement considéré comme le conducteur ?",
    options: [
      { text: "L'élève au volant", correct: false },
      { text: "L'accompagnateur (passager)", correct: true },
      { text: "L'école de conduite", correct: false },
      { text: "Personne, c'est un cas particulier", correct: false },
    ],
    explanation: "L'accompagnateur est CIVILEMENT considéré comme le conducteur. C'est pourquoi il doit être à jeun, alerte, et capable de reprendre les commandes à tout instant. En cas de retrait de points ou de sanction, c'est l'accompagnateur qui est visé.",
    engagement: "L'accompagnateur ne consomme JAMAIS d'alcool avant un trajet AAC, même « juste un verre ».",
    notes: "⏱️ 2 min. Slide clé pour les parents. Ils réalisent souvent à ce moment que leur responsabilité est réelle.",
  },

  /* ══════════ PAUSE 5 min ══════════ */
  {
    type: 'section',
    number: '⏸',
    title: 'Pause · 5 minutes',
    note: 'Café, toilettes, questions individuelles',
    notes: "⏱️ 5 min. Reste disponible en salle pour les questions gênantes en public. Souvent des parents osent poser leurs vraies questions à ce moment.",
  },

  /* ══════════ PHASE 7 · Bilan de séance (10 min) ══════════ */
  {
    type: 'bilan',
    title: "Ce qu'on retient de ce RVP1",
    retenir: [
      "L'AAC démarre à 15 ans · examen à 17 ans · conduite seule à 17 ans depuis 2024",
      "3 000 km minimum sur 1 an minimum + 3 vitesses spéciales (110/100/80)",
      "0,2 g/L MAX pendant tout le probatoire (loi 2015) · 0 verre recommandé",
      "L'accompagnateur reste passager responsable : jamais sous alcool ou substances",
      "Le livret d'apprentissage se tient à jour à chaque trajet",
      "Téléphone : 0 usage au volant, même Bluetooth oreillette (interdit depuis 2015)",
      "Fatigue : pause toutes les 2h + micro-sieste si signes d'endormissement",
    ],
    questions_ouvertes: [
      "Qu'avez-vous appris aujourd'hui que vous ne saviez pas ?",
      "Qu'est-ce qui vous inquiète encore ?",
      "Un engagement que vous souhaitez prendre haut et fort devant la famille ?",
    ],
    notes: "⏱️ 10 min.\n\nReprends les attentes notées en Phase 2 : « Marie voulait qu'on parle de l'assurance — on l'a fait. Est-ce clair ? »\n\nSi certaines attentes n'ont pas été couvertes → propose un rdv individuel.",
  },

  /* ══════════ PHASE 8 · Évaluation finale (10 min) ══════════ */
  {
    type: 'qcm',
    question: "Éval finale n°1 · Un conducteur en période probatoire (AAC ou classique) a droit à :",
    options: [
      { text: "0,5 g/L d'alcool dans le sang", correct: false },
      { text: "0,2 g/L maximum (loi 2015)", correct: true },
      { text: '0 g/L — aucune tolérance', correct: false },
      { text: '0,8 g/L comme les autres', correct: false },
    ],
    explanation: "Depuis 2015 : 0,2 g/L pour tout permis probatoire. Sanction : 6 points sur 6 = permis invalidé. Recommandation morale : 0 verre.",
    engagement: "En cas de doute → l'élève ET l'accompagnateur ne conduisent pas. Uber, taxi, un proche : toujours mieux que le drame.",
    notes: "⏱️ 2 min.",
  },
  {
    type: 'qcm',
    question: "Éval finale n°2 · Sur autoroute, à quelle vitesse maximale doit rouler un conducteur AAC ?",
    options: [
      { text: '130 km/h comme les autres', correct: false },
      { text: '110 km/h (règle probatoire)', correct: true },
      { text: '90 km/h', correct: false },
      { text: '120 km/h', correct: false },
    ],
    explanation: "110 km/h sur autoroute pendant TOUTE la période probatoire (AAC + 2 ans après le permis). Les 2 disques à l'arrière préviennent les autres usagers.",
    notes: "⏱️ 2 min.",
  },
  {
    type: 'qcm',
    question: "Éval finale n°3 · Combien de km minimum doit-on parcourir en phase accompagnée AAC ?",
    options: [
      { text: '1 500 km', correct: false },
      { text: '2 500 km', correct: false },
      { text: '3 000 km', correct: true },
      { text: '5 000 km', correct: false },
    ],
    explanation: "3 000 km MINIMUM sur 1 an MINIMUM. Ces 3000 km doivent être variés : nuit, pluie, autoroute, longs trajets, agglomération, campagne.",
    notes: "⏱️ 2 min.",
  },
  {
    type: 'engagement',
    title: "Notre engagement mutuel · Signature du duo",
    kicker: 'À signer avant de quitter la salle',
    items: [
      "L'élève s'engage à conduire uniquement en présence de l'accompagnateur autorisé et mentionné au contrat",
      "L'accompagnateur s'engage à ne consommer aucun alcool avant chaque trajet accompagné",
      "L'élève s'engage à respecter les vitesses AAC (110 / 100 / 80 / 50 km/h)",
      "Le duo s'engage à varier les conditions de conduite : nuit, pluie, autoroute, montagne",
      "Le duo s'engage à revenir pour le RVP 2 (~1000 km) et le RVP 3 (~3000 km)",
      "L'accompagnateur reste passager : il n'intervient sur le volant qu'en cas d'urgence absolue",
      "Le duo s'engage à ne JAMAIS toucher un téléphone au volant (même Bluetooth oreillette)",
    ],
    signature: 'Signature élève · Signature accompagnateur · Signature enseignant ECSR',
    notes: "⏱️ 4 min.\n\nFais signer PHYSIQUEMENT sur papier — pas juste oralement. La signature engage.\n\nChaque famille repart avec sa copie signée. Tu en gardes une pour ton dossier.",
  },

  /* ══════════ PHASE 9 · Message Sécurité Routière (5 min) ══════════ */
  {
    type: 'message-sr',
    kicker: 'À emporter chez vous',
    message: "« Sur la route, on ne se trompe pas pour soi seulement. On se trompe pour tous ceux qu'on croise. »",
    chiffre: {
      number: '3 267',
      label: 'personnes tuées sur les routes de France métropolitaine en 2022. Chacune était le fils, la fille, le père ou la mère de quelqu\'un.',
    },
    source: 'ONISR · Bilan Sécurité Routière 2022',
    notes: "⏱️ 5 min. LE MOMENT LE PLUS IMPORTANT DE LA SÉANCE.\n\nLaisse un LONG silence après avoir lu la citation. Ne pas commenter tout de suite.\n\nReprends ensuite chaque attente notée en Phase 2 : est-ce que le RVP y a répondu ? Sinon, propose de reprendre contact.\n\nDistribue le livret d'apprentissage à l'élève ET une copie de l'engagement signé à l'accompagnateur.\n\nRappelle la date approximative du RVP 2 : après ~1000 km parcourus.\n\nMerci · Bon apprentissage · Bonne route.",
  },
  {
    type: 'cta',
    title: 'Merci · Bonne route',
    description: "Vous avez maintenant tous les outils pour démarrer sereinement les 3000 km. Rendez-vous après environ 1000 km pour le RVP 2 : bilan de mi-parcours, conduite de nuit et autoroute.",
    next_action: 'On se retrouve vers 1000 km',
  },
];

/* ══════════ BLOC BONUS RVP 1 · Quiz d'ancrage + études de cas (à intégrer au besoin) ══════════ */
const RVP_1_QUIZ_BLOC = [
  {
    type: 'section',
    number: '🧠',
    title: "Quiz & études de cas — Ancrage",
    note: "Bloc bonus à activer selon le temps et l'engagement · 15 min",
  },
  {
    type: 'qcm',
    question: "En cas de contrôle routier pendant la conduite accompagnée, quels documents DOIVENT être présentés ?",
    options: [
      { text: "Uniquement le permis de l'accompagnateur", correct: false },
      { text: "Carte grise + AIPC (attestation d'inscription au permis) + permis de l'accompagnateur", correct: true },
      { text: "Livret d'apprentissage numérique + carte d'identité", correct: false },
      { text: "Attestation d'assurance uniquement", correct: false },
    ],
    explanation: "3 documents obligatoires : (1) Certificat d'immatriculation du véhicule, (2) Formulaire de demande de permis de conduire correspondant à l'AIPC (imprimé ou dématérialisé), (3) Permis de conduire de l'accompagnateur en cours de validité (ou ADCS). Le livret d'apprentissage seul NE SUFFIT PAS à justifier l'état d'élève conducteur.",
    notes: "⏱️ 3 min.\n\nRappelle : ces documents doivent être physiquement présents dans le véhicule à CHAQUE trajet. Fais un tour de salle : « Qui a l'AIPC dans son téléphone en version dématérialisée ? »",
  },
  {
    type: 'qcm',
    question: "Un ami de la famille souhaite être accompagnateur. Il a le permis depuis 6 ans mais a eu une invalidation il y a 3 ans pour perte totale de points. Peut-il accompagner ?",
    options: [
      { text: "Oui, il a récupéré ses 12 points depuis", correct: false },
      { text: "Non, il faut ne pas avoir eu de retrait de permis dans les 5 dernières années", correct: true },
      { text: "Oui, à condition d'avoir suivi un stage de récupération", correct: false },
      { text: "Oui, s'il n'a pas fait de délit routier grave", correct: false },
    ],
    explanation: "Article R211-5 · Formulation officielle Service-Public.gouv.fr : l'accompagnateur doit être titulaire du permis B depuis ≥ 5 ans SANS interruption ET ne pas avoir eu de retrait (annulation OU invalidation) dans les 5 dernières années. Ici : invalidation il y a 3 ans → INTERDIT jusqu'à 5 ans révolus.",
    notes: "⏱️ 3 min.\n\nPoint SENSIBLE. Insiste : « SANS interruption » veut dire aussi bien annulation qu'invalidation par perte totale de points. Suggestion : demander une attestation de droits à conduire sécurisée (ADCS) à chaque accompagnateur candidat.",
  },
  {
    type: 'qcm',
    question: "Pendant un trajet AAC, l'accompagnateur est-il concerné par les contrôles alcool/stupéfiants ?",
    options: [
      { text: "Non, il ne conduit pas donc aucun risque", correct: false },
      { text: "Oui, il peut être testé et encourt les MÊMES sanctions que s'il conduisait", correct: true },
      { text: "Oui, mais uniquement s'il touche au volant", correct: false },
      { text: "Non, seule la personne au volant (l'élève) est concernée", correct: false },
    ],
    explanation: "L'accompagnateur peut être soumis à un dépistage d'alcoolémie ou de substances stupéfiantes. En cas de contrôle positif, il encourt les MÊMES condamnations que s'il était au volant : retrait de points, amende, suspension… Le code lui interdit implicitement de boire avant/pendant tout trajet AAC.",
    notes: "⏱️ 3 min. Rappel juridique majeur pour les parents. Anecdote : un accompagnateur en fin de repas de famille qui prend la place « passager » pour laisser conduire son enfant AAC = risque de perdre son permis.",
  },
  {
    type: 'qcm',
    question: "Le 1er RDV pédagogique (hors RDV préalable) a lieu quand exactement ?",
    options: [
      { text: "Après les 3 000 km parcourus", correct: false },
      { text: "Entre 4 et 6 mois après la délivrance de l'AFFI", correct: true },
      { text: "Entre 6 et 8 mois après le début de l'AAC", correct: false },
      { text: "Immédiatement après le rendez-vous préalable", correct: false },
    ],
    explanation: "Source officielle Service-Public.gouv.fr (2025) : le 1er RDV pédagogique a lieu entre 4 et 6 mois après la délivrance de l'AFFI (attestation de fin de formation initiale). Le 2ᵉ RDV a lieu au moment des 3 000 km. Un 3ᵉ RDV facultatif peut être organisé.",
    notes: "⏱️ 2 min. Précision importante : l'ancien seuil « ~1000 km » n'est plus la référence officielle. Le déclencheur est le TEMPS (4-6 mois après AFFI), pas le kilométrage.",
  },
  {
    type: 'qcm',
    question: "Peut-on conduire en AAC avec une remorque attelée au véhicule ?",
    options: [
      { text: "Non, les remorques sont interdites en AAC", correct: false },
      { text: "Oui, mais la somme PTAC voiture + remorque doit ≤ 3 500 kg", correct: true },
      { text: "Oui, sans aucune restriction de poids", correct: false },
      { text: "Uniquement pour les remorques ≤ 500 kg", correct: false },
    ],
    explanation: "En AAC, on conduit avec un véhicule de catégorie B. Une remorque est autorisée. Si son PTAC est > 750 kg, la somme (PTAC voiture + PTAC remorque) ne doit pas dépasser 3 500 kg. C'est la règle du permis B classique.",
    notes: "⏱️ 2 min. Question terrain — beaucoup de familles ont une remorque pour bricolage/jardin. Rassure les parents : c'est tout à fait possible.",
  },
  {
    type: 'tour-de-table',
    title: "Étude de cas #1 · Le trajet familial de vacances",
    intro: "Direction les vacances : 800 km à parcourir, dont 600 km d'autoroute. Une partie du trajet se fera en conduite accompagnée.",
    questions: [
      "Élève : « Sur quelle portion je me sens le plus à l'aise pour prendre le volant en premier ? »",
      "Accompagnateur : « À quel moment je propose de faire une pause pour changer de conducteur ? »",
      "Duo : « Quelle est la vitesse max autorisée pour un conducteur en AAC sur cette autoroute limitée à 130 km/h ? » (Réponse : 110 km/h)",
      "Duo : « Si on passe une frontière par mégarde, que se passe-t-il pour l'assurance ? » (Réponse : garanties annulées en cas d'accident)",
    ],
    note: "Objectif : anticiper concrètement un cas réel que votre duo va probablement vivre cet été.",
    notes: "⏱️ 5 min.\n\nCe scénario est très concret. Encourage chaque famille à prévoir DÈS MAINTENANT qui conduit sur quelle portion. Rappel : conduite à l'étranger = INTERDITE (Belgique, Espagne, Suisse, Italie…).",
  },
  {
    type: 'tour-de-table',
    title: "Étude de cas #2 · La sortie d'école et le téléphone",
    intro: "Sortie d'école du petit frère à récupérer. Le duo AAC est dans la voiture, accompagnateur assis à l'avant. Un appel arrive sur le téléphone de l'accompagnateur — l'école qui prévient d'un retard.",
    questions: [
      "Accompagnateur : « Est-ce que je décroche pendant que la conduite est en cours ? »",
      "Élève : « Est-ce que je propose de m'arrêter pour laisser mon accompagnateur répondre ? »",
      "Duo : « Quelle est la règle : peut-on tenir un téléphone en main pendant que quelqu'un conduit à côté de nous ? »",
      "Duo : « Comment communiquer efficacement pendant un trajet — quel signal pour dire « je vais m'arrêter » ? »",
    ],
    note: "Objectif : ancrer la règle « pas de téléphone en main » et convenir d'un signal de communication.",
    notes: "⏱️ 5 min.\n\nL'accompagnateur AVEC téléphone en main pendant la conduite de l'élève = infraction. Beaucoup de familles ne le savent pas. Recommande le kit mains-libres ou le mode DND téléphone.",
  },
];

RVP_1_SLIDES.push(...RVP_1_QUIZ_BLOC);

/* ══════════════════════════════════════════════════════════════════
   RVP 2 — Bilan mi-parcours (~1000 km) · 3h (1h conduite + 2h théorie)
   Format : ~40 slides pour tenir 2h avec un groupe de 5-10 personnes
   ══════════════════════════════════════════════════════════════════ */

export const RVP_2_SLIDES = [
  /* ══════════ PHASE 1 · Accueil (5 min) ══════════ */
  {
    type: 'title',
    kicker: 'Rendez-vous pédagogique n°2',
    title: 'Bilan final',
    subtitle: "Bilan final après ≥ 3 000 km parcourus en AAC · 3h (1h conduite + 2h théorie)",
    footer: "Élève · Accompagnateur(s) · Enseignant ECSR",
    notes: "⏱️ 2 min. Accueille chaque famille. Vérifie que le livret d'apprentissage est bien tenu à jour.",
  },
  {
    type: 'content',
    kicker: 'Programme',
    title: "Ce que nous allons voir ensemble aujourd'hui",
    bullets: [
      "🚗 Partie A · 1h conduite d'évaluation avec l'enseignant",
      "📋 Partie B · 2h théorie collective — bilan + situations complexes",
      "🎤 Accueil + programme — 5 min",
      "🔎 Bilan des 3 000 km parcourus (retour familles) — 20 min",
      "🌙 Cours 1 · Conduite de nuit — 15 min",
      "🌧️ Cours 2 · Intempéries (pluie, brouillard, neige) — 15 min",
      "🛣️ Cours 3 · Autoroute AAC — 15 min",
      "😴 Cours 4 · Fatigue longs trajets — 10 min",
      "🎯 Feuille de route jusqu'à l'examen — 10 min",
      "✅ Éval finale + engagement — 15 min",
    ],
    footnote: "Programme conforme à l'Arrêté du 22 mars 1993 modifié. Pause de 5 min à mi-parcours.",
  },

  /* ══════════ PHASE 2 · Bilan des 3 000 km (20 min) ══════════ */
  {
    type: 'section',
    number: '01',
    title: 'Bilan des 3 000 km parcourus',
    note: "Analyser sans juger — l'accompagnateur témoigne, place ensuite à l'écoute · 20 min",
  },
  {
    type: 'stat',
    number: '3 000 km',
    label: 'parcourus — étape charnière de l\'AAC',
    source: 'REMC · annexe 3',
    context: "À ce stade, l'élève a déjà rencontré la majorité des situations urbaines. Il reste à travailler les conditions dégradées (nuit, intempéries) et les grandes distances (autoroute).",
  },
  {
    type: 'tour-de-table',
    title: "Retour d'expérience du duo",
    intro: "Chaque duo (élève + accompagnateur) témoigne à tour de rôle. Objectif : partager les bonnes pratiques, identifier les difficultés récurrentes.",
    questions: [
      "Élève · « Quel a été mon plus grand progrès sur ces 3 000 km ? »",
      "Accompagnateur · « Quelle situation m'a le plus stressé(e) ? »",
      "Duo · « Comment s'est passée la communication entre nous ? »",
      "Duo · « Y a-t-il eu des désaccords majeurs ? Comment on les a gérés ? »",
      "Élève · « Une situation où j'ai eu peur ? »",
    ],
    note: "Note les difficultés récurrentes pour y revenir dans les cours théoriques ci-dessous.",
    notes: "⏱️ 15 min.\n\nExercice CENTRAL du RVP2. Laisse chaque duo témoigner sans couper.\n\nSi une famille avoue un accrochage → dédramatise, propose une analyse constructive.",
  },
  {
    type: 'qcm',
    question: "Bilan · Selon vous, dans quelle situation avez-vous le plus progressé sur ces 3 000 km ?",
    options: [
      { text: 'En agglomération (ville, ronds-points)', correct: false },
      { text: 'Sur route (départementales)', correct: false },
      { text: 'En conduite de nuit', correct: false },
      { text: 'La gestion du stress au volant', correct: false },
    ],
    explanation: "Toutes les réponses sont VALIDES. Cette question sert à mesurer la diversité des expériences. Si toute la salle vote « agglomération », c'est un signal : il faut varier davantage les conditions.",
    notes: "⏱️ 3 min. Ce QCM est un outil DIAGNOSTIC — pas de bonne/mauvaise réponse. Utilise les résultats pour orienter la suite.",
  },
  {
    type: 'content',
    kicker: 'Bilan · Points d\'attention fréquents',
    title: "Ce qui pose souvent problème à ce stade",
    bullets: [
      "Les insertions sur voie rapide (encore trop lentes ou trop timorées)",
      "Le stationnement en créneau (manque d'expérience réelle en conditions variées)",
      "La vigilance qui baisse sur trajets routiniers (domicile-école, courses)",
      "La communication tendue avec l'accompagnateur (haussement de voix, stress)",
      "Le respect strict des 110 km/h en autoroute (tentation de suivre le flux)",
    ],
    footnote: "Ces points sont NORMAUX à ce stade. L'objectif du RVP2 est de les identifier pour les travailler d'ici l'examen.",
    notes: "⏱️ 3 min. Rassure : « Ce sont les difficultés de TOUS les élèves à ce stade. Vous n'êtes pas seuls. »",
  },

  /* ══════════ PHASE 3 · Conduite de nuit (15 min) ══════════ */
  {
    type: 'section',
    number: '02',
    title: 'Conduite de nuit',
    note: 'La visibilité change tout — anticipation × 2 · 15 min',
  },
  {
    type: 'stat',
    number: '50 %',
    label: 'des accidents mortels ont lieu la nuit',
    source: 'ONISR · Bilan 2022',
    context: "Alors que la nuit ne représente que 10 % du trafic. Fatigue, alcool, éblouissement et perception altérée en sont les causes principales.",
  },
  {
    type: 'content',
    kicker: 'Nuit — spécificités',
    title: "Ce qui change quand la nuit tombe",
    bullets: [
      "Fatigue accrue : baisse de vigilance dès 21h, pic entre 2h et 5h du matin",
      "Perception des distances altérée : les phares « écrasent » la profondeur",
      "Éblouissement au croisement : regarder à droite, pas dans les phares adverses",
      "Contrôle des feux avant chaque départ (croisement, route, position, brouillard)",
      "Distances de sécurité × 1,5 minimum",
      "Vitesse : réduire de 10 km/h par rapport à la limite de jour",
    ],
    footnote: "Le premier trajet de nuit doit se faire sur un itinéraire CONNU pour ne pas ajouter le stress de la navigation.",
    notes: "⏱️ 5 min.\n\nDemande aux élèves : « Combien de trajets de nuit avez-vous faits ? » Souvent 0 ou 1.\n\nInsiste : la nuit s'apprivoise progressivement. Pas de premier trajet en pleine autoroute nocturne.",
  },
  {
    type: 'qcm',
    question: "Nuit · À la tombée de la nuit, quand doit-on allumer les feux de croisement ?",
    options: [
      { text: 'Quand il fait totalement noir', correct: false },
      { text: "Dès qu'on commence à mal voir", correct: true },
      { text: 'À 22h en été, 18h en hiver', correct: false },
      { text: 'Quand les autres voitures les allument', correct: false },
    ],
    explanation: "L'allumage des feux se fait DÈS que la visibilité baisse (crépuscule, tunnel, pluie, brouillard). L'objectif est double : voir ET être vu. Les feux de position seuls ne suffisent pas en agglomération.",
    notes: "⏱️ 2 min.",
  },
  {
    type: 'qcm',
    question: "Nuit · Face à un véhicule en sens inverse mal éclairé (éblouissement), il faut :",
    options: [
      { text: 'Fixer les phares adverses pour bien les localiser', correct: false },
      { text: 'Regarder vers le bord DROIT de la route', correct: true },
      { text: 'Ralentir brusquement et freiner', correct: false },
      { text: 'Faire un appel de phares en réponse', correct: false },
    ],
    explanation: "En cas d'éblouissement, regarder vers le bord DROIT de la route (ligne blanche latérale) pour maintenir la trajectoire sans être ébloui. NE JAMAIS fixer les phares adverses. Ralentir progressivement si vraiment gêné.",
    notes: "⏱️ 2 min.",
  },

  /* ══════════ PHASE 4 · Intempéries (15 min) ══════════ */
  {
    type: 'section',
    number: '03',
    title: 'Conduite par intempéries',
    note: 'Pluie, brouillard, neige, verglas · 15 min',
  },
  {
    type: 'content',
    kicker: 'Pluie',
    title: "La pluie — le piège le plus courant",
    bullets: [
      "Aquaplaning : au-delà de 80 km/h sur route mouillée, risque réel de perte d'adhérence",
      "Distance de freinage × 1,5 minimum (jusqu'à × 2 sur route grasse)",
      "Vitesses limitées baissent de 20 km/h : 110→90 autoroute, 80→70 hors agglo",
      "Essuie-glaces + feux de croisement obligatoires dès les premières gouttes",
      "En cas de trombes d'eau : ralentir, feux de détresse un instant pour prévenir, s'arrêter si nécessaire",
    ],
    footnote: "L'aquaplaning se ressent : le volant devient « léger », les roues avant patinent. Réaction : LEVER LE PIED, ne pas freiner, garder le cap droit.",
    notes: "⏱️ 5 min.\n\nDécris l'aquaplaning en détail : c'est la sensation la plus déstabilisante pour un jeune conducteur. Prévenir = savoir gérer.",
  },
  {
    type: 'qcm',
    question: "Intempéries · Sur route mouillée, la distance de freinage est :",
    options: [
      { text: 'La même que sur route sèche', correct: false },
      { text: 'Environ 1,5 fois plus longue', correct: true },
      { text: '2 fois plus courte (l\'eau freine mieux)', correct: false },
      { text: 'Réduite si les pneus sont neufs', correct: false },
    ],
    explanation: "Sur route mouillée : distance × 1,5. Sur route grasse (première pluie après période sèche) : × 2. Sur verglas : × 5. À 90 km/h sur route mouillée, il faut ~85 m au lieu de 55 m sur sec.",
    notes: "⏱️ 2 min.",
  },
  {
    type: 'content',
    kicker: 'Brouillard + Neige',
    title: "Brouillard, neige : les règles-clés",
    bullets: [
      "Brouillard : feux de brouillard AVANT + ARRIÈRE (arrière : uniquement si visibilité < 50 m)",
      "Vitesse dépasse jamais la distance de visibilité (règle des 2 s : si tu vois à 40 m, tu roules < 40 km/h)",
      "Neige : équipements obligatoires en zones montagne du 1/11 au 31/03 (loi Montagne 2)",
      "Chaînes ou pneus hiver M+S selon les panneaux B26 (obligation stricte)",
      "En cas de dérapage sur neige/verglas : lâcher l'accélérateur, tourner LÉGÈREMENT dans le sens du dérapage",
    ],
    footnote: "Pas prêt(e) pour les intempéries ? RESTE À LA MAISON. La meilleure conduite est celle qu'on n'a pas eu à faire.",
    notes: "⏱️ 5 min. Insiste sur la loi Montagne 2 : c'est nouveau (2021) et beaucoup de familles ne le savent pas.",
  },
  {
    type: 'qcm',
    question: "Intempéries · Les feux de brouillard ARRIÈRE (rouges) doivent être allumés :",
    options: [
      { text: "Dès qu'il pleut", correct: false },
      { text: "Uniquement si visibilité < 50 m", correct: true },
      { text: 'La nuit sur autoroute', correct: false },
      { text: 'Toujours par précaution', correct: false },
    ],
    explanation: "Les feux de brouillard ARRIÈRE (très puissants, rouges) éblouissent les conducteurs derrière. Ils sont réservés au brouillard épais avec visibilité < 50 m ou chute de neige dense. Sinon = amende + gêne pour les autres.",
    notes: "⏱️ 2 min. Beaucoup de gens laissent leurs feux brouillard arrière allumés à tort — dangereux pour les autres.",
  },

  /* ══════════ PHASE 5 · Autoroute (15 min) ══════════ */
  {
    type: 'section',
    number: '04',
    title: 'Autoroute AAC',
    note: "Le premier grand trajet — préparation + concentration · 15 min",
  },
  {
    type: 'content',
    kicker: 'Autoroute AAC',
    title: "Les 5 règles d'or",
    bullets: [
      "Vitesse maximale : 110 km/h (règle probatoire) — même si le panneau indique 130",
      "Distances de sécurité : au minimum 2 secondes (au moins 60 m à 110 km/h)",
      "Pause obligatoire : toutes les 2h ou dès signes de fatigue",
      "Sortie prévue à l'avance : jamais de manœuvre de dernière minute",
      "Bande d'arrêt d'urgence : SEULEMENT en cas de panne — jamais pour téléphoner",
    ],
    footnote: "Le premier trajet autoroute doit se faire avec l'enseignant, PAS avec l'accompagnateur seul.",
    notes: "⏱️ 5 min.",
  },
  {
    type: 'qcm',
    question: "Autoroute · Sur autoroute, la distance de sécurité minimale se mesure en :",
    options: [
      { text: '10 mètres derrière le véhicule précédent', correct: false },
      { text: '2 secondes de temps de suivi', correct: true },
      { text: 'La longueur de sa voiture', correct: false },
      { text: 'Autant de mètres que la vitesse en km/h', correct: false },
    ],
    explanation: "Règle des 2 SECONDES : compter 2 secondes entre le passage du véhicule précédent devant un repère (panneau, arbre) et notre propre passage devant le même repère. À 110 km/h, cela représente environ 60 mètres. Sur pluie : passer à 3 secondes.",
    notes: "⏱️ 2 min. Fais l'exercice mental : « 1001, 1002 » = 2 secondes. Simple à mémoriser.",
  },
  {
    type: 'qcm',
    question: "Autoroute · Si le véhicule tombe en panne sur autoroute, la première action est :",
    options: [
      { text: 'Sortir immédiatement du véhicule pour appeler', correct: false },
      { text: "S'arrêter sur la bande d'arrêt d'urgence + feux détresse + gilet + tous derrière la glissière", correct: true },
      { text: 'Rester dans le véhicule et attendre les secours', correct: false },
      { text: 'Rouler doucement jusqu\'à la prochaine sortie', correct: false },
    ],
    explanation: "Protocole PANNE AUTOROUTE : (1) Bande d'arrêt d'urgence + feux détresse (2) Passer gilet AVANT de descendre (3) Tous les occupants derrière la glissière de sécurité (4) Appeler dépannage via bornes SOS ou 112. NE JAMAIS rester dans le véhicule sur bande d'arrêt d'urgence — les accidents graves y sont fréquents.",
    notes: "⏱️ 3 min. Slide capitale. Beaucoup de morts sur BAU chaque année parce que les gens restent dans la voiture.",
  },

  /* ══════════ PHASE 6 · Fatigue longs trajets (10 min) ══════════ */
  {
    type: 'section',
    number: '05',
    title: 'Fatigue sur longs trajets',
    note: 'Le tueur silencieux · 10 min',
  },
  {
    type: 'stat',
    number: '3 secondes',
    label: 'de micro-sommeil à 130 km/h = 108 m à l\'aveugle',
    source: 'ASFA · 2022',
    context: "Le micro-sommeil est involontaire. On ne réalise pas qu'on s'est endormi. À 130 km/h, 3 secondes = la longueur d'un stade. Aucune chance de réaction en cas d'obstacle.",
  },
  {
    type: 'content',
    kicker: 'Longs trajets',
    title: "Comment préparer un long trajet en AAC",
    bullets: [
      "Dormir 7-8h la nuit précédente (pas de trajet avec 4h de sommeil !)",
      "Départ à heure raisonnable (éviter départs 2h-6h du matin)",
      "Pause 15 min toutes les 2h — impérativement",
      "Hydratation régulière (soif = signe de fatigue)",
      "Éviter les repas lourds à midi si trajet l'après-midi",
      "Prévoir des relais de conduite si possible (autre accompagnateur agréé)",
    ],
    footnote: "La micro-sieste de 15-20 min sur aire d'autoroute est le seul remède efficace contre la somnolence. Ni café, ni musique, ni fenêtre ouverte.",
    notes: "⏱️ 4 min. Insiste : le café donne un coup de fouet 20 min, puis rebond en fatigue. Sieste = vrai remède.",
  },
  {
    type: 'qcm',
    question: "Longs trajets · Face à un premier bâillement au volant, la meilleure réaction est :",
    options: [
      { text: 'Boire un café fort au prochain péage', correct: false },
      { text: "S'arrêter dès l'aire suivante pour 15-20 min de sieste", correct: true },
      { text: 'Ouvrir la fenêtre pour se rafraîchir', correct: false },
      { text: 'Mettre de la musique forte', correct: false },
    ],
    explanation: "Le bâillement est le PREMIER signal de fatigue. La seule réaction efficace est la MICRO-SIESTE de 15-20 min. Café, air frais, musique forte n'ont qu'un effet placebo de courte durée et masquent la fatigue sans la traiter.",
    notes: "⏱️ 2 min.",
  },

  /* ══════════ PAUSE 5 min ══════════ */
  {
    type: 'section',
    number: '⏸',
    title: 'Pause · 5 minutes',
    note: 'Café, toilettes, questions individuelles',
  },

  /* ══════════ PHASE 7 · Feuille de route (10 min) ══════════ */
  {
    type: 'engagement',
    title: "Feuille de route jusqu'à l'examen",
    kicker: "5 objectifs concrets à atteindre avant le RVP3",
    items: [
      "Minimum 1 trajet autoroute de plus de 200 km",
      "Minimum 1 trajet de nuit d'au moins 30 minutes",
      "Minimum 1 trajet sous la pluie",
      "Test de reprise après une longue interruption (>3 semaines sans conduire)",
      "Point mensuel élève / accompagnateur sur les acquis et les points de vigilance",
    ],
    signature: 'Signature élève · Signature accompagnateur · Signature enseignant ECSR',
    notes: "⏱️ 5 min. Faites signer physiquement — les engagements écrits ont plus de valeur.",
  },

  /* ══════════ PHASE 8 · Éval finale (15 min) ══════════ */
  {
    type: 'qcm',
    question: "Éval finale n°1 · Sur autoroute en AAC, on doit rouler au maximum à :",
    options: [
      { text: '90 km/h', correct: false },
      { text: '110 km/h', correct: true },
      { text: '130 km/h', correct: false },
      { text: 'Le maximum du panneau', correct: false },
    ],
    explanation: "110 km/h en AAC (règle probatoire). Les disques CA à l'arrière signalent aux autres.",
    notes: "⏱️ 2 min.",
  },
  {
    type: 'qcm',
    question: "Éval finale n°2 · Face au brouillard épais (< 50 m de visibilité), on doit :",
    options: [
      { text: 'Allumer les feux de brouillard avant seulement', correct: false },
      { text: 'Allumer feux de croisement + brouillard avant + brouillard arrière', correct: true },
      { text: 'Rouler avec les feux de position uniquement', correct: false },
      { text: 'Allumer les pleins phares', correct: false },
    ],
    explanation: "Feux de croisement + brouillard AVANT + brouillard ARRIÈRE (obligatoires si visibilité < 50m). PAS de pleins phares qui provoqueraient un mur blanc devant vous par réflexion.",
    notes: "⏱️ 2 min.",
  },
  {
    type: 'qcm',
    question: "Éval finale n°3 · Sur route mouillée, la distance de sécurité doit être :",
    options: [
      { text: 'La même que sur route sèche', correct: false },
      { text: 'De 3 secondes (au lieu de 2)', correct: true },
      { text: 'Plus courte (l\'eau adoucit le freinage)', correct: false },
      { text: 'Zéro si les pneus sont neufs', correct: false },
    ],
    explanation: "Passer de 2 à 3 secondes sur mouillé. À 90 km/h, 3 s = 75 m. Anticipation × 2 sur pluie.",
    notes: "⏱️ 2 min.",
  },
  {
    type: 'bilan',
    title: "Ce qu'on retient de ce RVP2",
    retenir: [
      "Nuit : distance × 1,5 + regarder à droite en cas d'éblouissement",
      "Pluie : distance × 1,5 (mouillé) à × 2 (grasse) + réduction vitesse de 20 km/h",
      "Brouillard : feux brouillard arrière SEULEMENT si visibilité < 50 m",
      "Autoroute AAC : 110 km/h + 2 s de sécurité + pause toutes les 2h",
      "Panne sur BAU : gilet + tous derrière glissière + jamais dans le véhicule",
      "Fatigue : micro-sieste 15-20 min = seul vrai remède",
    ],
    questions_ouvertes: [
      "Depuis le RVP1, qu'est-ce qui a le plus évolué chez vous ?",
      "Une situation où le duo a bien fonctionné ?",
      "Une situation où vous auriez aimé être mieux préparé(e) ?",
    ],
    notes: "⏱️ 5 min.",
  },
  {
    type: 'cta',
    title: 'À bientôt pour le RVP 3',
    description: "Rendez-vous après 3000 km parcourus — bilan final et préparation à l'examen pratique.",
    next_action: 'Bonne route à toute la famille',
  },
];

/* ══════════ BLOC BONUS RVP 2 · Quiz d'ancrage + études de cas mi-parcours ══════════ */
const RVP_2_QUIZ_BLOC = [
  {
    type: 'section',
    number: '🧠',
    title: "Quiz mi-parcours & études de cas",
    note: "Bloc bonus à activer selon le temps · situations rencontrées après 1 000 km · 15 min",
  },
  {
    type: 'qcm',
    question: "Depuis le 1er mars 2024, avec l'AFFI en AAC + attestation AM, l'élève peut-il conduire une voiturette avant l'obtention du permis B ?",
    options: [
      { text: "Non, il faut attendre les 17 ans", correct: false },
      { text: "Oui, s'il récupère son attestation AM auprès de l'auto-école", correct: true },
      { text: "Uniquement le week-end", correct: false },
      { text: "Oui, sans aucun document supplémentaire", correct: false },
    ],
    explanation: "Depuis le 1er mars 2024 : un élève ayant suivi une formation AAC (et disposant de son AFFI) peut conduire une voiturette (quadricycle léger à moteur, catégorie AM) à condition d'avoir récupéré auprès de son auto-école son attestation AM (mentionnant le nombre d'heures de formation). Utile pour la mobilité pendant les 3 000 km.",
    notes: "⏱️ 3 min. Nouveauté récente peu connue. Aide énormément les familles rurales où l'élève n'a pas d'autre moyen de transport. Rappelle : uniquement une VOITURETTE (pas une voiture de catégorie B), et uniquement seul(e), pas en AAC.",
  },
  {
    type: 'qcm',
    question: "L'élève AAC a-t-il le droit de conduire seul(e) le véhicule familial sur un parking privé ?",
    options: [
      { text: "Oui, le parking privé n'est pas une voie publique", correct: false },
      { text: "Non, l'accompagnateur doit être présent à l'avant à chaque instant", correct: true },
      { text: "Oui, pour manœuvrer uniquement", correct: false },
      { text: "Oui, tant que le moteur tourne au ralenti", correct: false },
    ],
    explanation: "Article R211-5 : la conduite AAC se fait sous la surveillance CONSTANTE et DIRECTE de l'accompagnateur, assis à l'avant à côté de l'élève. Aucune exception, y compris sur un parking privé, dans une cour ou pour une manœuvre. L'assurance ne couvre pas les trajets sans accompagnateur.",
    notes: "⏱️ 3 min. Question fréquente des parents. Ferme la porte à toute tentation. Rappelle : si accident sans accompagnateur = pas d'assurance.",
  },
  {
    type: 'qcm',
    question: "L'élève est flashé à 92 km/h dans une zone limitée à 80. Qui reçoit l'amende et perd des points ?",
    options: [
      { text: "L'élève, comme n'importe quel conducteur", correct: false },
      { text: "L'accompagnateur perd les points, le propriétaire du véhicule reçoit l'amende", correct: true },
      { text: "Personne, l'élève n'a pas encore de permis", correct: false },
      { text: "L'auto-école, en tant que responsable pédagogique", correct: false },
    ],
    explanation: "L'apprenti n'a pas encore de permis, donc pas de points. En cas d'excès de vitesse ou de feu rouge, c'est l'ACCOMPAGNATEUR qui est sanctionné du nombre de points correspondant à l'infraction, et l'amende est envoyée au PROPRIÉTAIRE du véhicule. Toute contravention DOIT être notée dans le carnet de suivi (lu par l'examinateur).",
    notes: "⏱️ 3 min. Point crucial pour l'accompagnateur. Insiste : chaque infraction sera lue par l'examinateur — donc à noter, pas à cacher. Rappel des limitations : 110/100/100/80/50.",
  },
  {
    type: 'qcm',
    question: "Le véhicule AAC doit-il obligatoirement être équipé de doubles rétroviseurs latéraux ?",
    options: [
      { text: "Non, c'est facultatif", correct: false },
      { text: "Oui, un dispositif de rétroviseurs additionnels côté accompagnateur est obligatoire", correct: true },
      { text: "Uniquement sur autoroute", correct: false },
      { text: "Uniquement pour les véhicules > 10 ans", correct: false },
    ],
    explanation: "Le véhicule utilisé en conduite accompagnée doit être équipé d'un DISPOSITIF DE DOUBLES RÉTROVISEURS latéraux — c'est-à-dire un rétroviseur additionnel côté accompagnateur (généralement une extension ventouse ou clip). Il permet à l'accompagnateur d'anticiper les manœuvres et de compenser l'absence de doubles commandes.",
    notes: "⏱️ 2 min. Élément physique concret. Vérifie qui l'a bien installé. Coûte 10-20 € en ligne, souvent oublié.",
  },
  {
    type: 'tour-de-table',
    title: "Étude de cas #3 · Le rond-point de nuit sous la pluie",
    intro: "Trajet retour à 22h30 sous pluie battante. Arrivée sur un rond-point inconnu, mal éclairé. Deux voitures suivent de près, une troisième arrive du rond-point avec des phares éblouissants.",
    questions: [
      "Élève : « Quels indices je cherche en priorité pour évaluer la situation ? »",
      "Accompagnateur : « Comment je peux t'aider sans te faire paniquer ? »",
      "Duo : « Faut-il ralentir davantage à cause de la pluie ? De combien ? »",
      "Duo : « Si tu doutes de la priorité, que faire ? » (Réponse : marquer un vrai temps d'arrêt)",
    ],
    note: "Objectif : faire verbaliser une situation stressante déjà vécue et convenir d'une méthodologie commune.",
    notes: "⏱️ 5 min.\n\nSituation typique post-3 000 km. Encourage chaque duo à raconter une situation similaire vécue. La verbalisation collective apaise l'appréhension et transmet des astuces entre familles.",
  },
  {
    type: 'tour-de-table',
    title: "Étude de cas #4 · L'accompagnateur qui s'endort",
    intro: "Long trajet dominical de 3h. Après 2h de conduite, l'accompagnateur (grand-père, 65 ans) commence à somnoler sur le siège passager.",
    questions: [
      "Élève : « Est-ce que je continue à conduire ou je m'arrête ? »",
      "Accompagnateur : « Suis-je encore en situation de surveillance CONSTANTE et DIRECTE ? »",
      "Duo : « Quelles sont les conséquences si un accident survient à ce moment-là ? »",
      "Duo : « Comment prévenir cette situation à l'avenir ? »",
    ],
    note: "Objectif : rappeler que la surveillance doit être ACTIVE, pas passive. L'endormissement de l'accompagnateur = fin légale de l'AAC.",
    notes: "⏱️ 5 min.\n\nSujet TRÈS important trop souvent négligé. Si l'accompagnateur dort, l'élève N'EST PLUS en situation légale d'AAC — assurance non applicable en cas d'accident. Recommandation : pauses toutes les 2 h, café, changement de conducteur.",
  },
  {
    type: 'tour-de-table',
    title: "Étude de cas #5 · L'accident matériel léger",
    intro: "Manœuvre de créneau qui tourne mal : accrochage de l'aile arrière d'une voiture stationnée. Personne n'est blessé mais il y a des dégâts matériels sur les 2 véhicules.",
    questions: [
      "Élève : « Qui remplit le constat amiable ? Moi ou mon accompagnateur ? »",
      "Accompagnateur : « Comment expliquer la situation d'apprentissage à l'autre conducteur ? »",
      "Duo : « Quels documents fournir : mon AIPC, le permis de l'accompagnateur, ou les deux ? »",
      "Duo : « Faut-il déclarer ce sinistre à l'auto-école ? Pourquoi ? »",
    ],
    note: "Objectif : dédramatiser l'incident matériel et clarifier la procédure administrative.",
    notes: "⏱️ 5 min.\n\nLe constat est rempli par l'accompagnateur (garde juridique du véhicule pendant l'AAC). L'élève est passager au sens de la loi. Déclarer à l'auto-école = OUI, pour la traçabilité pédagogique et pour l'examinateur.",
  },
];

RVP_2_SLIDES.push(...RVP_2_QUIZ_BLOC);

/* ══════════════════════════════════════════════════════════════════
   RVP 3 — Bilan final (~3000 km) · 3h (1h conduite + 2h théorie)
   Format : ~40 slides — prépa examen + engagement responsable
   ══════════════════════════════════════════════════════════════════ */

export const RVP_3_SLIDES = [
  /* ══════════ PHASE 1 · Accueil (5 min) ══════════ */
  {
    type: 'title',
    kicker: 'Rendez-vous pédagogique n°3 · Final',
    title: 'Bilan final',
    subtitle: "3000 km parcourus — préparation à l'examen pratique · 3h (1h simulation + 2h théorie)",
    footer: "Élève · Accompagnateur(s) · Enseignant ECSR",
    notes: "⏱️ 2 min. Moment solennel — le duo est arrivé au bout des 3000 km. Reconnais l'effort accompli.",
  },
  {
    type: 'content',
    kicker: 'Programme',
    title: "Ce que nous allons voir ensemble",
    bullets: [
      "🚗 Partie A · 1h simulation d'examen pratique avec l'enseignant",
      "📋 Partie B · 2h théorie collective",
      "🎤 Accueil + programme — 5 min",
      "🔎 Bilan des 3000 km (retour familles) — 20 min",
      "⚖️ Cours 1 · Permis probatoire (2 ans en AAC) — 15 min",
      "📝 Cours 2 · Déroulé de l'examen pratique — 20 min",
      "🛡️ Cours 3 · Prévention post-permis — 15 min",
      "🎯 Cours 4 · Conduite sur longue distance — 10 min",
      "✅ Éval finale + engagement de conduite responsable — 20 min",
      "🛡️ Message SR — 5 min",
    ],
    footnote: "Programme conforme à l'Arrêté du 22 mars 1993 modifié. Pause 5 min à mi-parcours.",
  },

  /* ══════════ PHASE 2 · Bilan des 3000 km (20 min) ══════════ */
  {
    type: 'section',
    number: '01',
    title: 'Bilan des 3000 km',
    note: "Synthèse du duo et de l'école · 20 min",
  },
  {
    type: 'stat',
    number: '3000 km',
    label: "kilométrage minimal atteint — l'élève est prêt(e)",
    source: 'REMC · annexe 3',
    context: "Vous avez traversé les 4 saisons, roulé de nuit, sur autoroute, sous la pluie. Ce parcours vous distingue statistiquement des jeunes conducteurs de filière classique.",
  },
  {
    type: 'tour-de-table',
    title: "Retour d'expérience sur les 3000 km",
    intro: "Chaque duo témoigne de son parcours. C'est le moment de célébrer, mais aussi d'identifier les fragilités qu'il faudra travailler à l'examen.",
    questions: [
      "Élève · « Ma plus grande fierté depuis le RVP1 ? »",
      "Élève · « Un domaine où je me sens encore fragile ? »",
      "Accompagnateur · « Le moment où j'ai été le plus fier(e) de l'élève ? »",
      "Duo · « Qu'est-ce qui a le mieux marché dans notre collaboration ? »",
      "Duo · « Ce que vous partageriez à un futur duo AAC ? »",
    ],
    note: "Note les fragilités récurrentes pour les travailler à l'examen.",
    notes: "⏱️ 15 min. Exercice ÉMOTIONNEL. Laisse les familles célébrer. C'est un accomplissement rare.",
  },
  {
    type: 'stat',
    number: '- 25 %',
    label: "d'accidents chez les jeunes conducteurs issus de l'AAC vs filière classique",
    source: 'ONISR · Bilan pluriannuel',
    context: "L'AAC réduit statistiquement de 25 % le risque d'accident sur les 2 premières années post-permis. C'est votre bouclier statistique.",
  },

  /* ══════════ PHASE 3 · Permis probatoire (15 min) ══════════ */
  {
    type: 'section',
    number: '02',
    title: 'Permis probatoire',
    note: "Ce que change le fait d'avoir fait l'AAC · 15 min",
  },
  {
    type: 'content',
    kicker: 'Probatoire AAC',
    title: "Vos avantages vs filière classique",
    bullets: [
      "Durée probatoire : 2 ans (au lieu de 3 en filière classique)",
      "6 points initialement, +2 après 1 an sans infraction, +2 après 2 ans → 12 points",
      "Vitesses réduites toujours applicables pendant la durée probatoire (110/100/80)",
      "Alcool : 0,2 g/L max (loi 2015) pendant tout le probatoire · 0 verre recommandé",
      "En cas de retrait de 3 points ou plus : stage obligatoire (à vos frais, ~250 €)",
    ],
    footnote: "En AAC, les 12 points sont atteints en 2 ans (au lieu de 3 en filière classique).",
    notes: "⏱️ 5 min. Célèbre le bénéfice AAC. Les élèves méritent de savoir ce qu'ils ont gagné.",
  },
  {
    type: 'qcm',
    question: "Probatoire · Combien de points a un conducteur AAC à la fin de sa période probatoire (2 ans sans infraction) ?",
    options: [
      { text: '6 points', correct: false },
      { text: '8 points', correct: false },
      { text: '12 points', correct: true },
      { text: '18 points', correct: false },
    ],
    explanation: "Le capital de points d'un permis probatoire monte progressivement : 6 → 8 → 10 → 12. Pour l'AAC, la période est de 2 ans donc les 12 points sont atteints en 2 ans. Pour la filière classique, c'est 3 ans.",
    notes: "⏱️ 2 min.",
  },
  {
    type: 'qcm',
    question: "Probatoire · Un conducteur AAC en 1re année de permis commet un excès > 40 km/h. Que se passe-t-il ?",
    options: [
      { text: 'Perte de 3 points, sans autre conséquence', correct: false },
      { text: 'Perte de 6 points = permis invalidé + stage obligatoire', correct: true },
      { text: '135 € d\'amende uniquement', correct: false },
      { text: 'Rien, seulement une remontrance', correct: false },
    ],
    explanation: "Excès > 40 km/h = 6 points + suspension immédiate. En probatoire (6 points), c'est le PERMIS INVALIDÉ + tout à refaire (théorique + pratique). C'est pourquoi rouler à 110 en AAC est capital — pas de « 130 comme les autres ».",
    notes: "⏱️ 3 min. Message DUR mais nécessaire. Le probatoire ne pardonne pas.",
  },
  {
    type: 'content',
    kicker: 'Assurance post-permis',
    title: "Choisir son assurance jeune conducteur",
    bullets: [
      "Vérifier auprès de l'assureur familial le bonus AAC (souvent -15 % la 1re année)",
      "Comparer AU MINIMUM 3 devis (ex : comparateurs en ligne)",
      "Ne pas choisir « au tiers » à tout prix : les frais en cas d'accident responsable sont énormes",
      "Franchise : plus elle est basse, plus la cotisation est haute — trouver le juste équilibre",
      "Assistance panne 0 km : quasi indispensable pour un jeune conducteur",
    ],
    notes: "⏱️ 3 min.",
  },

  /* ══════════ PHASE 4 · Examen pratique (20 min) ══════════ */
  {
    type: 'section',
    number: '03',
    title: 'Examen pratique',
    note: 'Ce qui vous attend le jour J · 20 min',
  },
  {
    type: 'content',
    kicker: "Jour de l'examen",
    title: "Comment se déroule l'épreuve",
    bullets: [
      "Durée : 32 minutes minimum (dont 25 minutes de conduite effective)",
      "Trajet imposé par l'inspecteur, incluant au moins une voie rapide ou route à double sens",
      "1 arrêt de précision + 1 manœuvre (créneau, marche arrière, épi, bataille)",
      "1 question de vérification technique intérieure + 1 extérieure",
      "1 question de premiers secours",
      "5 minutes de conduite autonome (« Rejoignez telle destination sans indications »)",
    ],
    footnote: "Nombre de points requis pour réussir : 20/31 (avec au moins 1 point sur les critères éliminatoires).",
    notes: "⏱️ 5 min. Chaque élève a en général une image vague de l'examen. Précise sans dramatiser.",
  },
  {
    type: 'content',
    kicker: "Compétences évaluées",
    title: "Les 4 grandes compétences notées",
    bullets: [
      "1. Installation au poste de conduite + départ + arrêt (2 pts)",
      "2. Utilisation des commandes (2 pts) — direction, boîte, freinage",
      "3. Prise d'information (3 pts) — regard, rétros, angles morts",
      "4. Adaptation à la circulation (5 pts) — vitesse, distance, insertion, dépassement",
      "+ Autonomie (1 pt), courtoisie (1 pt), écoconduite (1 pt), respect règlementation (3 pts)",
    ],
    footnote: "Chaque compétence est notée 0, 1, 2 ou 3. Total sur 31. Passage à 20/31 minimum.",
    notes: "⏱️ 5 min. Détaille pour dédramatiser : ce n'est pas « je conduis parfaitement », c'est « je maîtrise dans les grandes lignes ».",
  },
  {
    type: 'qcm',
    question: "Examen · Combien de points sont nécessaires pour obtenir le permis ?",
    options: [
      { text: '15/31', correct: false },
      { text: '18/31', correct: false },
      { text: '20/31', correct: true },
      { text: '25/31', correct: false },
    ],
    explanation: "20 points minimum sur 31, à condition de ne pas commettre d'erreur ÉLIMINATOIRE (feu rouge grillé, refus de priorité grave, mise en danger).",
    notes: "⏱️ 2 min.",
  },
  {
    type: 'content',
    kicker: 'Astuces jour J',
    title: "Les 7 conseils qui font la différence",
    bullets: [
      "Bien dormir la veille — pas de soirée arrosée !",
      "Petit-déjeuner solide 2h avant : cerveau ET estomac contents",
      "Arriver 15 min en avance pour se poser + toilettes",
      "Écouter attentivement la consigne de l'inspecteur AVANT de démarrer",
      "Dire ses intentions à voix haute (« Je change de voie ») → prouve la prise d'information",
      "En cas d'erreur : rester calme, corriger immédiatement, ne pas s'excuser 10 fois",
      "Sortie de virage, sortie de rond-point : ACCÉLÉRER — l'inspecteur veut voir une conduite dynamique",
    ],
    notes: "⏱️ 5 min.",
  },
  {
    type: 'qcm',
    question: "Examen · Pendant les 5 minutes de conduite autonome, l'élève doit :",
    options: [
      { text: "Suivre exactement l'itinéraire donné par l'inspecteur", correct: false },
      { text: "Rejoindre la destination en autonomie, en choisissant son itinéraire", correct: true },
      { text: "Rouler en silence sans regarder l'inspecteur", correct: false },
      { text: "Demander conseil à l'inspecteur en cas de doute", correct: false },
    ],
    explanation: "Autonomie = 5 min où l'élève CHOISIT son itinéraire pour aller à un point donné (« Rendez-vous à la gare »). L'inspecteur évalue la prise d'initiative, la lecture des panneaux et la gestion de trajectoire.",
    notes: "⏱️ 2 min.",
  },

  /* ══════════ PHASE 5 · Prévention post-permis (15 min) ══════════ */
  {
    type: 'section',
    number: '04',
    title: 'Prévention post-permis',
    note: 'Les pièges des 2 premières années · 15 min',
  },
  {
    type: 'stat',
    number: '× 3',
    label: "risque d'accident les 6 premiers mois post-permis",
    source: 'ONISR · Étude 2021',
    context: "Effet « sortie de cocon » : sans accompagnateur, sans enseignant, la vigilance chute. Les 6 premiers mois post-permis sont statistiquement les plus dangereux.",
  },
  {
    type: 'content',
    kicker: 'Post-permis · Les 5 pièges',
    title: "Ce qui fait échouer les jeunes permis",
    bullets: [
      "🎉 Trajets festifs avec passagers du même âge (× 2 risque accident)",
      "📱 Réutilisation du téléphone parce que « je conduis seul(e), personne ne me voit »",
      "💨 Vitesse : oubli des 110 km/h en autoroute (habitude familiale à 130 km/h)",
      "😴 Longs trajets étudiants (retour week-end fatigant, sans pause)",
      "🍷 Un « juste 1 verre » à une soirée d'anniversaire = alcool positif garanti",
    ],
    footnote: "Ces 5 pièges causent 80 % des accidents graves chez les jeunes permis.",
    notes: "⏱️ 5 min.",
  },
  {
    type: 'qcm',
    question: "Post-permis · Après votre permis, à un anniversaire, on vous sert 1 verre de vin. Que faites-vous ?",
    options: [
      { text: "Je le bois, 1 verre ce n'est pas grave", correct: false },
      { text: "Je le décline poliment et je bois de l'eau", correct: true },
      { text: "Je le bois puis attends 30 min avant de conduire", correct: false },
      { text: "Je bois puis prends un café pour compenser", correct: false },
    ],
    explanation: "En probatoire (2 ans AAC), le taux légal est 0,2 g/L max. UN verre standard = ~0,20 g/L = atteint la limite. Recommandation morale : 0 verre. Sanction : 6 points sur 6 = permis invalidé. Le seul choix responsable = eau, jus de fruit, ou soft.",
    engagement: "Je m'engage à refuser tout alcool avant de conduire pendant tout mon probatoire (2 ans).",
    notes: "⏱️ 3 min.",
  },
  {
    type: 'content',
    kicker: 'Post-permis · Bonne pratique',
    title: "Le contrat familial post-permis (recommandé)",
    bullets: [
      "L'élève informe TOUJOURS la famille de son trajet + heure d'arrivée estimée",
      "En cas de sortie festive : ticket taxi/Uber payé par la famille (~15-30 €) — cadeau qui sauve",
      "Message à l'arrivée : « Bien rentré(e) » = tranquillité pour tous",
      "Interdiction stricte de prendre le volant après 22h les 6 premiers mois (sauf trajet quotidien)",
      "Réévaluation mensuelle du contrat : ce qui marche, ce qui coince",
    ],
    footnote: "Ce contrat n'est pas une punition — c'est un cadre bienveillant qui prolonge le rôle de l'accompagnateur.",
    notes: "⏱️ 3 min.",
  },

  /* ══════════ PHASE 6 · Conduite longue distance (10 min) ══════════ */
  {
    type: 'section',
    number: '05',
    title: 'Longue distance en solo',
    note: '10 min · Le voyage étudiant, les vacances, les week-ends',
  },
  {
    type: 'content',
    kicker: 'Voyage solo',
    title: "Checklist départ voyage",
    bullets: [
      "Sommeil : 7-8h la nuit précédente",
      "Véhicule : pression pneus + niveau huile + liquide lave-glace",
      "Documents : permis + carte grise + assurance + gilet + triangle",
      "Itinéraire : préparé avant + pauses planifiées (toutes les 2h)",
      "Téléphone : chargé + mode « Ne pas déranger au volant » activé",
      "Météo : consulter avant de partir → adaptation trajet ou horaire si besoin",
    ],
    footnote: "Application recommandée : « Bison Futé » (info trafic) + « Michelin Météo » (météo trajet).",
    notes: "⏱️ 4 min.",
  },
  {
    type: 'qcm',
    question: "Voyage · Sur autoroute, l'espacement de repos MAXIMUM entre 2 pauses est :",
    options: [
      { text: '1 heure', correct: false },
      { text: '2 heures ou 200 km', correct: true },
      { text: '4 heures', correct: false },
      { text: 'Aucune limite si on se sent bien', correct: false },
    ],
    explanation: "Pause OBLIGATOIRE toutes les 2h ou 200 km, MÊME sans signe de fatigue. La vigilance baisse insidieusement — on ne le sent pas. 5-10 min sur aire suffisent : marcher, boire de l'eau, s'étirer.",
    notes: "⏱️ 2 min.",
  },

  /* ══════════ PAUSE 5 min ══════════ */
  {
    type: 'section',
    number: '⏸',
    title: 'Pause · 5 minutes',
    note: 'Café, toilettes, questions individuelles',
  },

  /* ══════════ PHASE 7 · Éval finale (10 min) ══════════ */
  {
    type: 'qcm',
    question: "Éval finale n°1 · En AAC, la période probatoire dure :",
    options: [
      { text: '1 an', correct: false },
      { text: '2 ans', correct: true },
      { text: '3 ans', correct: false },
      { text: '5 ans', correct: false },
    ],
    explanation: "2 ans pour AAC (au lieu de 3 en filière classique). Vos avantages : 12 points atteints en 2 ans, pas 3.",
    notes: "⏱️ 1 min.",
  },
  {
    type: 'qcm',
    question: "Éval finale n°2 · Excès > 40 km/h en probatoire :",
    options: [
      { text: 'Amende sans retrait de point', correct: false },
      { text: '3 points retirés', correct: false },
      { text: '6 points = permis invalidé', correct: true },
      { text: 'Suspension 1 mois seulement', correct: false },
    ],
    explanation: "6 points = permis invalidé en probatoire (6 points de départ). Tout à refaire.",
    notes: "⏱️ 1 min.",
  },
  {
    type: 'qcm',
    question: "Éval finale n°3 · Combien de points sont nécessaires pour réussir l'examen pratique ?",
    options: [
      { text: '18/31', correct: false },
      { text: '20/31', correct: true },
      { text: '25/31', correct: false },
      { text: '30/31', correct: false },
    ],
    explanation: "20/31 minimum, sans erreur éliminatoire (feu rouge grillé, priorité grave).",
    notes: "⏱️ 1 min.",
  },

  /* ══════════ PHASE 8 · Engagement final (10 min) ══════════ */
  {
    type: 'engagement',
    title: 'Engagement de conduite responsable',
    kicker: "À signer avant de quitter la salle — valable pour les 2 ans de probatoire",
    items: [
      "Je m'engage à respecter les vitesses probatoires pendant 2 ans (110/100/80)",
      "Je m'engage à 0,2 g/L max (recommandation : 0 verre) pendant tout mon probatoire",
      "Je m'engage à ne pas utiliser mon téléphone au volant (même Bluetooth oreillette)",
      "Je m'engage à faire une pause toutes les 2h sur les longs trajets",
      "Je m'engage à informer ma famille de mes trajets festifs (Uber/taxi si alcool)",
      "En cas de doute ou de fatigue : je ne conduis pas, je demande à quelqu'un ou je prends un taxi",
    ],
    signature: 'Signature élève · Signature accompagnateur · Signature enseignant ECSR',
    notes: "⏱️ 5 min. Faire signer physiquement. Chaque famille repart avec une copie.",
  },
  {
    type: 'bilan',
    title: "Ce qu'on retient de ces 3 RVP",
    retenir: [
      "L'AAC = 3000 km sur 1 an minimum + 3 RVP obligatoires",
      "Probatoire AAC = 2 ans + 12 points atteints par paliers",
      "Alcool 0,2 g/L max en probatoire (loi 2015) + téléphone jamais + vitesses réduites = trio gagnant",
      "Examen : 20/31 minimum + éviter erreurs éliminatoires",
      "Post-permis : 6 premiers mois = période à risque × 3 (rester vigilant)",
      "L'accompagnateur reste une ressource : appelle en cas de doute",
    ],
    questions_ouvertes: [
      "Quelle est votre plus grande fierté depuis le RVP1 ?",
      "Un souvenir marquant à partager avec le groupe ?",
      "Un engagement fort que vous voulez prendre publiquement ?",
    ],
    notes: "⏱️ 5 min.",
  },

  /* ══════════ PHASE 9 · Message SR final (5 min) ══════════ */
  {
    type: 'message-sr',
    kicker: 'À emporter chez vous',
    message: "« Le vrai permis, ce n'est pas celui que l'inspecteur signe. C'est celui qu'on se donne, chaque matin, en montant dans la voiture. »",
    chiffre: {
      number: '2 ans',
      label: 'de probatoire — 730 jours pendant lesquels chaque trajet compte. Ensuite, la responsabilité reste à vie.',
    },
    source: 'Sécurité Routière · Campagne 2024',
    notes: "⏱️ 5 min. Moment de clôture. Reconnais l'accomplissement de chaque famille.",
  },
  {
    type: 'cta',
    title: 'Bonne route !',
    description: "Vous êtes prêt(e) pour l'examen. Merci pour cette aventure — votre enseignant ECSR reste à disposition après le permis pour toute question ou stage de perfectionnement.",
    next_action: 'À vous de jouer',
  },
];

/* ══════════════════════════════════════════════════════════════════
   AM · Séquence 7 · SYNTHÈSE avec les parents (1h obligatoire)
   Arrêté du 8 novembre 2012 modifié · Formation AM (BSR cyclo 50cc / voiturette)
   Format : ~30 slides pour tenir 1h avec parent(s) + élève(s)
   ══════════════════════════════════════════════════════════════════ */

export const AM_SYNTHESE_SLIDES = [
  /* ══════════ PHASE 1 · Accueil (5 min) ══════════ */
  {
    type: 'title',
    kicker: 'Formation AM · Séquence 7 · Synthèse',
    title: 'Bienvenue',
    subtitle: "7e et dernière séquence obligatoire · 1h · avec parents",
    footer: "Élève · Parent(s) obligatoirement présents · Enseignant ECSR",
    notes: "⏱️ 2 min.\n\nAccueille chaleureusement. Le parent est un maillon essentiel : c'est lui/elle qui pourra rappeler les règles à la maison.\n\nVérifie que le livret de formation est présent + les 6 séquences précédentes signées.",
  },
  {
    type: 'content',
    kicker: 'Séquence 7 · Programme',
    title: "Ce que nous allons voir ensemble en 1h",
    bullets: [
      "🎤 Accueil & tour de table — 5 min",
      "🔎 Bilan de la formation AM (7h précédentes) — 10 min",
      "🛡️ Équipements obligatoires + recommandés — 10 min",
      "⚠️ Les 5 dangers spécifiques du 2-roues motorisé — 15 min",
      "🍷 Alcool · téléphone · fatigue · vitesse — 10 min",
      "✅ Engagement famille + remise du livret — 10 min",
    ],
    footnote: "Formation AM = 8h min · Arrêté du 8 novembre 2012 modifié. Le certificat AM permet de conduire dès 14 ans un cyclo 50 cm³ (≤ 45 km/h) ou un quadricycle léger (voiturette).",
    notes: "⏱️ 3 min. Rappelle : cette dernière séquence est OBLIGATOIRE pour valider la formation. Sans parent = pas de certificat.",
  },

  /* ══════════ PHASE 2 · Bilan de la formation (10 min) ══════════ */
  {
    type: 'section',
    number: '01',
    title: 'Bilan de la formation',
    note: "Retour sur les 7h précédentes · 10 min",
  },
  {
    type: 'stat',
    number: '8 heures',
    label: "de formation obligatoire pour obtenir le certificat AM",
    source: "Arrêté du 8 novembre 2012 modifié",
    context: "3h hors circulation (maîtrise du véhicule sur plateau) + 4h en circulation (mise en situation réelle) + 1h de synthèse avec parents. Formation valable pour cyclo 50 cm³ ET voiturettes.",
  },
  {
    type: 'tour-de-table',
    title: "Retour d'expérience de l'élève",
    intro: "Temps de partage sur les 7h de formation. Le parent écoute. Objectif : identifier les acquis et les fragilités.",
    questions: [
      "Élève · « Quel a été le moment le plus dur de la formation ? »",
      "Élève · « Ma plus grande fierté ? »",
      "Élève · « Une situation où j'ai eu peur ? »",
      "Parent · « Qu'est-ce que je retiens de ce que tu me racontes depuis 7h ? »",
      "Parent · « Qu'est-ce qui me préoccupe le plus ? »",
    ],
    note: "Vos points sensibles nourriront directement la théorie qui suit.",
    notes: "⏱️ 7 min.\n\nMoment CLÉ pour créer le lien parent-formateur. Beaucoup de parents découvrent à ce moment ce que leur ado a vécu.\n\nSi une peur ressort → connecte avec un des dangers spécifiques abordés ensuite.",
  },

  /* ══════════ PHASE 3 · Équipements (10 min) ══════════ */
  {
    type: 'section',
    number: '02',
    title: 'Équipements du conducteur',
    note: "Le 2e casque du conducteur = son corps · 10 min",
  },
  {
    type: 'content',
    kicker: "Équipements OBLIGATOIRES",
    title: "Ce qui est légalement requis",
    bullets: [
      "Casque homologué (norme ECE 22.05 ou 22.06) — jugulaire toujours attachée",
      "Gants CE homologués « moto » — obligatoires depuis 2016 pour conducteur ET passager",
      "Feu de croisement allumé en permanence (jour ET nuit)",
      "Gilet haute visibilité à bord (à porter en cas de panne)",
      "Plaque d'immatriculation lisible + assurance visible",
    ],
    footnote: "Casque non attaché ou gants absents = 135 € d'amende + 3 points (pour le titulaire du permis B qui prête le véhicule).",
    notes: "⏱️ 4 min.\n\nMontre un casque + des gants CE si tu peux. Rien de plus concret que le vrai matériel.\n\nInsiste : le gant CE est OBLIGATOIRE, pas seulement recommandé. Un gant en tissu ne suffit pas.",
  },
  {
    type: 'content',
    kicker: "Équipements RECOMMANDÉS",
    title: "Ce qui sauve la vie même s'il n'est pas légalement requis",
    bullets: [
      "Blouson moto CE (dorsale + protections coudes/épaules) — évite 80 % des blessures thoraciques",
      "Pantalon moto renforcé (ou jean CE) — pas de short en scooter !",
      "Bottes ou chaussures montantes couvrant la cheville — sneakers = fracture garantie en chute",
      "Casque INTÉGRAL plutôt que jet (protège le menton = zone très souvent fracturée)",
      "Vêtements clairs ou fluo — visibilité doublée à distance",
    ],
    footnote: "En cas de chute à 45 km/h : sans protection = brûlures profondes sur 30 % du corps. Avec équipement complet = quelques bleus.",
    notes: "⏱️ 4 min.\n\nParle du blouson : 90 € en solde, sauve une opération à 20 000 €. Le calcul est vite fait.\n\nDémonte le mythe « je ne vais pas loin » : la majorité des accidents ont lieu à moins de 5 km du domicile.",
  },
  {
    type: 'qcm',
    question: "Équipements · Le port des gants CE moto est-il obligatoire ?",
    options: [
      { text: "Non, uniquement recommandé", correct: false },
      { text: "Oui, mais seulement pour le conducteur", correct: false },
      { text: "Oui, pour le conducteur ET le passager", correct: true },
      { text: "Oui, mais uniquement en hiver", correct: false },
    ],
    explanation: "Depuis novembre 2016, les gants CE (marquage CE spécifique moto) sont OBLIGATOIRES pour tous — conducteur ET passager — sur cyclo, moto, scooter. Sanction : 135 € + 3 points sur le permis B (si prêt du véhicule à un mineur AM).",
    notes: "⏱️ 2 min. Beaucoup pensent encore que c'est facultatif. C'est FAUX depuis 2016.",
  },

  /* ══════════ PHASE 4 · Dangers spécifiques (15 min) ══════════ */
  {
    type: 'section',
    number: '03',
    title: 'Les 5 dangers du 2-roues',
    note: "Ce qui rend le cyclo si vulnérable · 15 min",
  },
  {
    type: 'stat',
    number: '× 22',
    label: "risque d'être tué en 2-roues motorisé vs voiture",
    source: 'ONISR · Bilan 2022',
    context: "Les 2-roues motorisés représentent 1,9 % du trafic mais 24 % des tués. Un jeune conducteur AM est particulièrement exposé : moins d'expérience + véhicule fragile.",
  },
  {
    type: 'content',
    kicker: 'Danger 1 · Invisibilité',
    title: "Personne ne te voit sur la route",
    bullets: [
      "Silhouette étroite = automobilistes ne te repèrent pas dans leurs rétros",
      "Angle mort : positionne-toi TOUJOURS dans le rétroviseur du conducteur devant toi",
      "Aux intersections : contact visuel avec l'automobiliste avant de démarrer",
      "Vêtements clairs + gilet réfléchissant = visibilité × 2 la nuit",
      "Feu de croisement allumé en permanence, même en plein jour",
    ],
    footnote: "« Je ne l'ai pas vu » : phrase entendue dans 60 % des accidents 2-roues avec voiture.",
    notes: "⏱️ 3 min.",
  },
  {
    type: 'content',
    kicker: 'Danger 2 · La chute',
    title: "Perte d'équilibre = contact direct avec le sol",
    bullets: [
      "Une flaque d'huile, une plaque d'égout mouillée, du gravier = chute immédiate",
      "Freinage trop brutal roue avant = blocage + chute sur le côté",
      "Virage trop rapide sur route humide = perte d'adhérence",
      "En cas de chute : ROULER SUR LE CÔTÉ (ne pas rester crispé sur le guidon)",
      "Ne JAMAIS relever le scooter si tu es blessé — mets-toi à l'abri et appelle le 112",
    ],
    footnote: "L'équipement transforme une chute grave en simple frayeur. Sans équipement : rare de s'en sortir sans opération.",
    notes: "⏱️ 3 min.",
  },
  {
    type: 'content',
    kicker: 'Danger 3 · Angle mort',
    title: "Les 2 zones où tu disparais complètement",
    bullets: [
      "Zone morte à DROITE de chaque véhicule (surtout camions) — jamais s'y engager",
      "Zone morte à GAUCHE d'une voiture qui tourne à droite (le conducteur ne regarde qu'à droite)",
      "Aux feux tricolores : ne JAMAIS se glisser entre 2 voitures en file",
      "Sur les ronds-points : rester au CENTRE de la voie, jamais au bord droit",
      "Croiser le regard du conducteur = seule garantie d'être vu",
    ],
    footnote: "Un camion de 30 tonnes ne te laisse aucune chance en cas de choc : reste toujours hors de ses zones mortes.",
    notes: "⏱️ 3 min. Montre au tableau la vue depuis un camion — dessine les zones mortes. Choc visuel.",
  },
  {
    type: 'content',
    kicker: 'Danger 4 · Coup latéral',
    title: "Une portière qui s'ouvre = 6 mètres de projection",
    bullets: [
      "Toujours anticiper qu'une portière PEUT s'ouvrir sur ton passage",
      "Distance de sécurité latérale minimum : 1 mètre (largeur de porte ouverte)",
      "Ne JAMAIS remonter une file par la droite (interdit + très dangereux)",
      "Attention aux voitures qui viennent de se garer (portière imminente)",
      "Aux feux : rester derrière la ligne, pas au niveau des roues avant",
    ],
    footnote: "Un cycliste ou un cyclomotoriste qui percute une portière ouverte à 30 km/h est projeté à 6 m devant lui.",
    notes: "⏱️ 3 min.",
  },
  {
    type: 'content',
    kicker: 'Danger 5 · Route mouillée',
    title: "L'adhérence chute drastiquement sous la pluie",
    bullets: [
      "Distance de freinage × 2 sur mouillé pour un 2-roues (vs × 1,5 en voiture)",
      "Marquages au sol (bandes blanches) = glissants comme du verglas",
      "Plaques d'égout métalliques + pavés = jamais y freiner",
      "Première pluie après période sèche = route grasse (huile + poussière) = pire",
      "Réduire nettement sa vitesse et doubler la distance de sécurité",
    ],
    footnote: "Sur route mouillée, un scooter à 45 km/h met 25 mètres pour s'arrêter (vs 10 mètres sur sec).",
    notes: "⏱️ 3 min.",
  },
  {
    type: 'qcm',
    question: "Dangers · En scooter, aux ronds-points, il faut se positionner :",
    options: [
      { text: "Sur le bord droit pour laisser passer les voitures", correct: false },
      { text: "Au CENTRE de la voie pour être bien visible", correct: true },
      { text: "Sur le bord gauche pour tourner plus vite", correct: false },
      { text: "Où on veut, ce n'est pas important", correct: false },
    ],
    explanation: "Au CENTRE de la voie ! Cela empêche les voitures de te doubler dangereusement dans le rond-point et te rend VISIBLE. Sur le bord droit = tu disparais dans l'angle mort + risque de choc latéral avec une voiture qui sort.",
    notes: "⏱️ 2 min. Contre-intuitif mais capital. Beaucoup d'élèves croient qu'ils doivent « faire de la place ».",
  },

  /* ══════════ PHASE 5 · Alcool · Téléphone · Fatigue · Vitesse (10 min) ══════════ */
  {
    type: 'section',
    number: '04',
    title: 'Alcool · Téléphone · Fatigue · Vitesse',
    note: "Les 4 tueurs classiques · 10 min",
  },
  {
    type: 'content',
    kicker: 'Point 1 · Alcool en AM',
    title: "Alcool : les vraies règles pour un jeune conducteur AM",
    bullets: [
      "AVANT 18 ans : vente ET consommation d'alcool INTERDITES en France (Code de la santé publique)",
      "Taux légal au volant en France : 0,5 g/L (0,2 g/L si période probatoire pour un autre permis)",
      "Le certificat AM ne crée PAS de « période probatoire » : c'est la limite classique qui s'applique",
      "Recommandation vitale pour un 2-roues : 0 verre (l'équilibre + les réflexes = zéro marge)",
      "Sanction si conduite alcoolisée : amende, retrait du certificat AM, interdiction de le repasser pendant 3 ans",
      "En scooter : effet alcool ×2 vs voiture (équilibre + réflexes plus critiques + fragilité corporelle)",
      "Fêtes ados : prévoir taxi / parent qui vient chercher AVANT la soirée",
    ],
    footnote: "Attention : l'alcool est INTERDIT à l'achat et à la consommation pour les mineurs en France (loi Bachelot 2009). Le débat n'est donc pas « combien de verres » mais « pas de verre du tout ».",
    notes: "⏱️ 3 min. Rappelle : le certificat AM ne « donne » pas 12 points au conducteur. C'est le futur permis B qui aura 6 → 12.\n\nSois FACTUEL : l'alcool est interdit aux mineurs. Point. Pas besoin d'inventer des règles fausses.",
  },
  {
    type: 'content',
    kicker: 'Points 2-3-4 · Distraction, fatigue, vitesse',
    title: "Ce qui tue les jeunes en 2-roues motorisé",
    bullets: [
      "📱 Téléphone en main = même sanction que voiture (135 € + 3 pts) — mais en scooter = mort quasi certaine à la moindre embardée",
      "😴 Fatigue : ne jamais conduire après 22h ou après une longue journée d'école",
      "💨 Vitesse : cyclomoteur limité à 45 km/h par construction. DÉBRIDER = jusqu'à 1 500 € d'amende + confiscation du véhicule",
      "🎧 Écouteurs interdits au guidon (135 € + 3 pts) — sons de la route = ta 2e paire d'yeux",
      "👥 Passager sur cyclomoteur 50cc : autorisé UNIQUEMENT si siège homologué + repose-pieds passager",
    ],
    footnote: "Le mythe du scooter débridé : c'est la voie royale vers l'accident. 60 km/h en scooter 50cc = zéro chance de rattraper l'imprévu.",
    notes: "⏱️ 5 min. Sujet SENSIBLE : les ados adorent débrider. Sois FERME sans être moralisateur.",
  },
  {
    type: 'qcm',
    question: "Vitesse · Un cyclomoteur 50cc est légalement limité à :",
    options: [
      { text: "30 km/h", correct: false },
      { text: "45 km/h", correct: true },
      { text: "50 km/h", correct: false },
      { text: "60 km/h", correct: false },
    ],
    explanation: "45 km/h maximum par construction (Directive européenne). Débrider = contravention de 5e classe : jusqu'à 1 500 € d'amende (3 000 € en récidive) + confiscation possible du véhicule. Sans compter la responsabilité civile en cas d'accident.",
    notes: "⏱️ 2 min.",
  },

  /* ══════════ PHASE 6 · Engagement & remise du livret (10 min) ══════════ */
  {
    type: 'section',
    number: '05',
    title: 'Engagement famille & Remise du livret',
    note: "Le contrat final · 10 min",
  },
  {
    type: 'engagement',
    title: "Engagement famille AM · Signature du duo parent + élève",
    kicker: 'À signer avant de quitter la salle',
    items: [
      "L'élève s'engage à porter le CASQUE + GANTS CE à chaque trajet, sans exception",
      "L'élève s'engage à conduire à 0 verre d'alcool tant qu'il/elle est mineur(e) — la loi française l'interdit",
      "L'élève s'engage à NE JAMAIS débrider son cyclomoteur (limité à 45 km/h)",
      "L'élève s'engage à ne pas utiliser son téléphone ni d'écouteurs au guidon",
      "Le parent s'engage à vérifier régulièrement l'état du véhicule (freins, éclairage, pneus)",
      "Le parent s'engage à ne prêter le cyclo qu'à un titulaire du certificat AM valide",
      "Le duo s'engage à s'appeler pour tout doute ou trajet festif (parent = solution de repli)",
    ],
    signature: 'Signature élève · Signature parent(s) · Signature enseignant ECSR',
    notes: "⏱️ 5 min.\n\nFais signer PHYSIQUEMENT. Ce contrat n'est pas une punition, c'est un cadre bienveillant.\n\nChaque famille repart avec sa copie.",
  },
  {
    type: 'bilan',
    title: "Ce qu'on retient de cette formation AM",
    retenir: [
      "Casque + gants CE OBLIGATOIRES (pas de tolérance)",
      "Cyclo 50cc = 45 km/h max — débrider = jusqu'à 1 500 € d'amende + confiscation du véhicule",
      "Alcool INTERDIT aux mineurs (loi Bachelot 2009) · au volant : 0,5 g/L max (0,2 si probatoire)",
      "Vulnérabilité 2-roues : 22 fois plus de risque d'être tué qu'en voiture",
      "5 dangers spécifiques : invisibilité · chute · angles morts · portières · pluie",
      "Aux ronds-points : rester au CENTRE de la voie",
    ],
    questions_ouvertes: [
      "Quel danger t'a le plus marqué aujourd'hui ?",
      "Un engagement que tu prends devant ton parent ?",
      "Parent · une inquiétude à partager ?",
    ],
    notes: "⏱️ 3 min.",
  },

  /* ══════════ PHASE 7 · Message SR + remise (5 min) ══════════ */
  {
    type: 'message-sr',
    kicker: 'À emporter chez soi',
    message: "« Sur un 2-roues, ton corps est ton pare-chocs. La seule protection qui existe, c'est celle que tu mets sur toi avant de partir. »",
    chiffre: {
      number: '24 %',
      label: 'des tués sur la route en France sont des usagers de 2-roues motorisé. Alors que ces véhicules ne représentent que 1,9 % du trafic.',
    },
    source: 'ONISR · Bilan Sécurité Routière 2022',
    notes: "⏱️ 3 min. Moment de clôture. Silence après la citation.\n\nRemets le livret de formation à l'élève + le certificat AM au parent (garant pour les 3 ans à venir).",
  },
  {
    type: 'cta',
    title: 'Certificat AM · Bonne route',
    description: "Ton certificat AM t'accompagne pour au moins 3 ans. En cas d'infraction grave, il peut t'être retiré. Prends-en soin — c'est ta clé de la mobilité.",
    next_action: 'Bonne route en toute sécurité',
  },
];

/* ══════════ BIBLIOTHÈQUE VIDÉOS · À projeter selon la salle ══════════
 * 7 vidéos YouTube optionnelles disponibles dans tous les RVP.
 * Le formateur navigue vers celle(s) qu'il souhaite montrer selon l'engagement de la salle.
 */
export const VIDEOS_BIBLIOTHEQUE = [
  {
    type: 'section',
    number: '🎬',
    title: 'Bibliothèque vidéo — à projeter selon la salle',
    note: '7 vidéos disponibles · à choisir selon le profil des participants et le temps disponible',
  },
  {
    type: 'video',
    youtube_id: '4ZXflb-0QAo',
    title: 'Vidéo 1',
    note: "Utilise cette vidéo pour lancer un débat en salle. Fais une pause après visionnage : « Qu'est-ce qui vous a le plus marqué ? »",
    notes: "⏱️ Prévoir 3 à 5 min de visionnage + 5 min d'échange. Passe cette slide si tu n'as pas le temps.",
  },
  {
    type: 'video',
    youtube_id: '6M37IYPibPA',
    title: 'Vidéo 2',
    note: "À projeter pour ancrer un message émotionnel fort. Silence après diffusion pour laisser respirer.",
    notes: "⏱️ 3-5 min. Idéale en fin de bloc pour marquer les esprits.",
  },
  {
    type: 'video',
    youtube_id: 'G0dDsMvGQT4',
    title: 'Vidéo 3',
    note: "À montrer aux parents/accompagnateurs pour illustrer leur rôle.",
    notes: "⏱️ 3-5 min. Bien pour redynamiser une salle après un bloc théorique dense.",
  },
  {
    type: 'video',
    youtube_id: 'pdg6OZz7mH0',
    title: 'Vidéo 4',
    note: "Vidéo pour introduire un point sensible. Demande aux participants d'imaginer la scène avant de lancer.",
    notes: "⏱️ 3-5 min. Peut remplacer un tour de table si l'ambiance est timide.",
  },
  {
    type: 'video',
    youtube_id: 'bIMPyxC6R-w',
    title: 'Vidéo 5',
    note: "À utiliser en clôture d'un bloc pour synthétiser visuellement le message.",
    notes: "⏱️ 3-5 min. Impact émotionnel — parfait avant la pause.",
  },
  {
    type: 'video',
    youtube_id: 'iaTc5WP_kRQ',
    title: 'Vidéo 6',
    note: "À adapter selon la salle : plus efficace avec des groupes déjà sensibilisés.",
    notes: "⏱️ 3-5 min. Vérifie le son avant projection.",
  },
  {
    type: 'video',
    youtube_id: '-s5k8xOx6Aw',
    title: 'Vidéo 7',
    note: "Vidéo de conclusion. Pour finir sur une note positive ou un rappel fort.",
    notes: "⏱️ 3-5 min. Peut servir de générique de fin avant le slide « Merci ».",
  },
  {
    type: 'video',
    youtube_id: 'wXpyNElACPM',
    title: 'Vidéo 8',
    note: "Vidéo bonus — à projeter selon l'engagement de la salle ou en ouverture d'un débat.",
    notes: "⏱️ 3-5 min. Vérifie le son avant projection. Idéale pour appuyer un point sensible ou relancer l'attention.",
  },
  {
    type: 'video',
    youtube_id: 'Ot1M9q4YFS4',
    title: 'Vidéo 9',
    note: "Vidéo bonus — impact émotionnel fort. À utiliser avec précaution selon l'âge et la sensibilité du groupe.",
    notes: "⏱️ 3-5 min. Prépare un temps de parole après visionnage — laisse la salle exprimer ses réactions.",
  },
];

/* Registry pour récupération par slug */
// Slide QR code de fin (générique) — ajouté juste avant la bibliothèque vidéo
const buildQrQuiz = (slug, code) => ({
  type: 'qr-quiz',
  slug,
  kicker: `Prolonger la séance · ${code}`,
  title: 'Refaire le quiz en famille',
  subtitle: "Scannez ce QR code avec votre téléphone pour retrouver toutes les questions du quiz — à faire tranquillement chez vous, parents et élève ensemble.",
  footnote: "Aucun compte requis · gratuit · illimité",
});

// Slide QR « Séance complète » — envoie sur le portable la totalité du contenu (textes, QCM, vidéos)
const buildQrSeance = (slug, code) => ({
  type: 'qr-quiz',
  url: (typeof window !== 'undefined' ? window.location.origin : 'https://codevoiture.fr') + `/seance/${slug}`,
  kicker: `📱 Recevez la séance sur votre téléphone · ${code}`,
  title: 'Revoyez la séance à la maison',
  subtitle: "Scannez pour retrouver l'intégralité du contenu : les textes, les vidéos et les questions interactives. À revoir tranquillement en famille — parents et élève, à votre rythme.",
  footnote: "Aucun compte · lien partageable · fonctionne aussi sur ordinateur",
});

export const RVP_CONTENT = {
  'aac-rdv-prealable': [...RDV_PREALABLE_SLIDES, ...GUIDE_AAC_PREALABLE_SLIDES, buildQrSeance('aac-rdv-prealable', 'RDV Préalable'), buildQrQuiz('aac-rdv-prealable', 'RDV Préalable'), ...VIDEOS_BIBLIOTHEQUE],
  'aac-rvp-1': [...RVP_1_SLIDES, ...GUIDE_AAC_RVP1_SLIDES, buildQrSeance('aac-rvp-1', 'AAC RVP 1'), buildQrQuiz('aac-rvp-1', 'AAC RVP 1'), ...VIDEOS_BIBLIOTHEQUE],
  'aac-rvp-2': [...RVP_2_SLIDES, ...GUIDE_AAC_RVP2_SLIDES, buildQrSeance('aac-rvp-2', 'AAC RVP 2'), buildQrQuiz('aac-rvp-2', 'AAC RVP 2'), ...VIDEOS_BIBLIOTHEQUE],
  'aac-rvp-3': [...RVP_3_SLIDES, buildQrSeance('aac-rvp-3', 'AAC RVP 3'), buildQrQuiz('aac-rvp-3', 'AAC RVP 3'), ...VIDEOS_BIBLIOTHEQUE],
  'am-synthese': [...AM_SYNTHESE_SLIDES, buildQrSeance('am-synthese', 'AM Synthèse'), buildQrQuiz('am-synthese', 'AM Synthèse'), ...VIDEOS_BIBLIOTHEQUE],
};
